/**
 * Customer-side product detail page (Wave C).
 */
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import FreshaStar from '../../Components/FreshaStar';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiAlertCircle, FiArrowLeft, FiArrowRight, FiCheck, FiChevronLeft, FiChevronRight, FiEdit3, FiShoppingCart, FiHeart, FiPlay, FiPhone, FiTruck } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { isVideoUrl } from '../../utils/mediaType';
import { brandName } from '../../utils/productBrands';
import heartIcon from '../../assets/heart.svg';
import exportIcon from '../../assets/export.svg';
import basketIcon from '../../assets/basket.svg';
import plusIcon from '../../assets/plus.svg';
import minusIcon from '../../assets/minus.svg';
import {
  fetchPublicProduct, getProductLikeState, likeProduct, unlikeProduct, trackStoreEvent,
  addToWishlist, removeFromWishlist, fetchShopPromotions, beforeStoreEventsLeave,
} from '../../api/storeApi';
import ProductCarousel from '../../Components/ProductCarousel';
import FreeShippingBar from '../../Components/FreeShippingBar';
import { useOptionalCart } from '../../Context/CartContext';
import useFreeDeliverySpend from '../../hooks/useFreeDeliverySpend';
import ColorPickerModal from './ColorPickerModal';
import ProductMediaViewer from '../../Components/ProductMediaViewer';
import { getBasePath } from '../../utils/domainMode';
import { productPath, productIdFromParam } from '../../utils/productUrl';
import { backFromHere } from './backFromHere';
import useRouteSalonSlug from '../../utils/useRouteSalonSlug';
import useDocumentTitle, { productTabTitle, shopEntityName } from '../../hooks/useDocumentTitle';
import BusinessNotFound from '../../Components/BusinessNotFound/BusinessNotFound';
import ReviewsModal from './ReviewsModal';
import ReviewsSection from './ReviewsSection';
import ShopReviewsBlock from './ShopReviewsBlock';
import ShopReviewsPriceChip from './ShopReviewsPriceChip';
import { StarsRow } from './ReviewCard';
import ShippingInfoSection from './ShippingInfoSection';
import DeliveryPromiseLine from './DeliveryPromiseLine';
import LazyRelated from './LazyRelated';
import ProductSaleCard from './ProductSaleCard';
import { getProductReviewSummary, getSalonBySlug, getBusinessRating } from '../../api/storeApi';
import { toImageUrl } from '../../Store/Actions/getSalon';
import {
  axisNameIn, localizedProduct, notePromptOf, optionValueShown, variantOptionsIn,
} from '../../utils/shopperText';
import { addRecentlyViewed } from '../../utils/recentSearch';
import { linkChoosesOnThisOpen, variantIdInLink, variantOpenedByLink } from '../../utils/variantLink';
import { recordProductOpen } from '../Shop/hotProducts';
import { campaignLinePricing, effectiveVariantPrice } from '../../utils/shopPromotions';
import { fromPrice, tilePrices } from '../../utils/choicePrices';
import { trackViewContent } from '../../utils/metaPixel';
import OrderOfferNote from '../../Components/OrderOfferNote';
import PrivacyNotice from '../../Components/PrivacyNotice';
import { galleryImages } from '../../utils/productGallery';
import { choiceWidget, isColourChoice, pictureOwner, tilePicture, valuesWithoutOwnPicture } from '../../utils/optionPictures';
import { swatchColour, swatchLetter } from '../../utils/colourSwatch';
import { tileLabelLines, tileLabelReserve } from '../../utils/tileLabel';
import { renditionUrl } from '../../utils/imageRenditions';
import { journeyCartAdd, journeyGallery, journeyVariant } from '../../utils/shopJourney';
import { hasCap, variantPurchaseCap } from '../../utils/stockCap';
import { useSalonWebTheme } from '../../hooks/useSalonWebTheme';
import useCoverTint from '../../hooks/useCoverTint';
import useTintVars from '../../hooks/useTintVars';
import usePageBackgroundMode from '../../hooks/usePageBackgroundMode';
import ProductBrandRow from './ProductBrandRow';
import { claimAutoLeave, handoffUrl, leaveForBrowser, openInBrowserHref, osOf } from '../../utils/openInBrowser';
import { PRODUCT_PAGE_ASKS_TO_OPEN_THE_BROWSER } from '../../utils/productPagePrompt';
import { currentAdVisit } from '../../utils/adVisit';
import { isInAppBrowser } from '../../utils/stableViewport';
import ContactOrderFallback from '../../Components/ContactOrderFallback';
import InAppBrowserPrompt from '../../Components/InAppBrowserPrompt/InAppBrowserPrompt';
import OfficialImporterTick from '../Shop/OfficialImporterTick';
import RichDescription from './RichDescription';
import SpecificationsSection from './SpecificationsSection';
import WarrantySection from './WarrantySection';
import PolicyDocumentLink from './PolicyDocumentLink';
import {
  warrantyForDisplay, returnsForDisplay, hasProse, returnsSummaryLine,
} from '../../utils/shopPromises';
import PolicyProse from './PolicyProse';
import { buildProductSpecs } from '../../utils/productSpecs';
import {
  EMPTY_BUSINESS_RATING, normaliseBusinessRating, formatRatingValue,
} from '../../utils/businessRating';
import { pageTintForMode } from '../../utils/coverTint';
import { resolveShopCoverUrl } from '../../utils/shopCover';
import { currencySymbol, groupedShopAmount, shopPrice } from '../../utils/currency';
import { contactOrderChannels, productOrderMessage } from '../../utils/contactOrder';
import { isAskProduct, hidesItsPrice } from '../../utils/orderMode';
import { useCurrency, useSetSalonCurrency } from '../../Context/CurrencyContext';
import StatusDot from '../../Components/StatusDot';
import { rememberCurrentProductName } from '../../utils/currentShop';
import usePublishedHeight from '../../hooks/usePublishedHeight';
import styles from './styles.module.css';
import { checkoutArm } from '../../utils/checkoutExperiment';
import { getVisitorKey } from '../../utils/visitorKey';

/**
 * Whether the customer has asked their system to stop moving things. Read at
 * the moment of the gesture rather than subscribed to, because the only
 * caller is a one-shot `scrollIntoView` and a stale subscription is a worse
 * answer than a fresh query. `hooks/useCoverFadeOnScroll.js` keeps the same
 * five lines for the same reason; they are not worth a shared module until a
 * third caller wants one, and a `try` around `matchMedia` is not optional —
 * jsdom and old WebViews both throw on an unsupported query.
 */
const prefersReducedMotion = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return !!window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (_) {
    return false;
  }
};

/**
 * HOW LONG THE PAGE WAITS BEFORE IT READS THE SCREEN, for the two things that hand the shopper to
 * another app: the WhatsApp order and the jump to the phone's own browser. A page that has gone
 * hidden by then lost the screen to something else; one still in front of the shopper did not. Long
 * enough for an app switch to have happened, short enough that the shopper has not moved on.
 */
const HANDOVER_RESULT_MS = 1500;

const ProductDetail = () => {
  const { t, i18n } = useTranslation();
  const isRtl = ['ar', 'he'].includes(String(i18n.language || '').split('-')[0]);
  const navigate = useNavigate();
  /* THE ADDRESS CARRIES THE NAME NOW, and the id is the tail of it:
   * `/p/<name-slug>-<uuid>`. Resolving happens HERE, at the one place the route
   * param is read, so every one of the ~40 uses below keeps receiving the bare
   * id it always did. `productIdFromParam` also answers a bare uuid, which is
   * every link minted before this scheme — in Google's index, in shared
   * messages and in this page's own tests. */
  const { productId: productParam } = useParams();
  const productId = useMemo(() => productIdFromParam(productParam), [productParam]);
  /* THE ADDRESS THE PAGE WAS OPENED AT, for the variant a link names (`?variant=`, an ad's colour).
   * Read by the product fetch below when it starts, and deliberately not one of its reasons to run:
   * a query string that changes under the page is not a different product. */
  // The whole location, not only its search: `key` is how we know whether
  // there is anything behind us in the shop. See backFromHere.js.
  const location = useLocation();
  /* The ROUTER's path, not `window.location.pathname`. This is a single-page app: the window's path is
   * whatever the browser last committed, and in a memory router it is "/" — so a hand-off built from it
   * sends the shopper to the shop's front page instead of the product they were looking at. Caught by the
   * test, which is the only place the two ever differ. */
  const { search: locationSearch, pathname: routePathname } = location;
  const openedSearchRef = useRef(locationSearch);
  openedSearchRef.current = locationSearch;
  // `/:salonSlug/p/:productId` on the platform, `/p/:productId` on a business's
  // own domain — where the slug comes from the host instead. Without it this
  // page never asked for the product at all and showed "Product not found" on
  // the domain the business hands to its customers.
  const { salonSlug, salonSlugUnresolved } = useRouteSalonSlug();
  useSalonWebTheme(salonSlug);
  const ctxCurrency = useCurrency();
  const setSalonCurrency = useSetSalonCurrency();
  const ForwardChevron = isRtl ? FiChevronLeft : FiChevronRight;
  const BackIcon = isRtl ? FiArrowRight : FiArrowLeft;
  const cart = useOptionalCart(salonSlug);

  /* THE PRODUCT AS THE SERVER SENT IT, and the product as THIS reader reads it (utils/shopperText). Everything the
   * page draws reads `product` — the name, the description, the category and the choices' names already in the page's
   * language — so a new `product.name` on this page cannot be the shop's language by accident. Values, ids, prices and
   * photos are the server's own; the address is minted from the shop's own name (`addressName`). */
  const [rawProduct, setProduct] = useState(null);
  const product = useMemo(() => localizedProduct(rawProduct, i18n.language), [rawProduct, i18n.language]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [pickedColor, setPickedColor] = useState(null);
  const [pickedSize, setPickedSize] = useState(null);
  const [pickedStyle, setPickedStyle] = useState(null);
  /* WHICH AXES THE SHOPPER HAS ACTUALLY TOUCHED.
   *
   * The page picks the first variant on load so the price and the photographs
   * have something to show — and that pick used to go straight into the cart.
   * On DK Store's KOALA set ("בשני ציפויים לבחירה") the plating picker stood
   * below the SUMMER15 box, off a 390px screen, and one tap on the sticky bar
   * bought "ציפוי זהב" for a shopper who never saw there was a choice. An axis
   * with more than one option on the shelf is not chosen until it is tapped;
   * see `pendingAxes` below and the gate at the top of `handleAddToCart`. */
  const [chosenAxes, setChosenAxes] = useState(() => new Set());
  /* THE AD'S LINK CHOSE FOR THE SHOPPER: every axis counts as chosen while this holds. It holds only on the
   * link's first open in this browser session (utils/variantLink `linkChoosesOnThisOpen`), and ends at the
   * shopper's first tap in any picker — from then on only the axes they tapped are chosen. */
  const [linkChose, setLinkChose] = useState(false);
  /** A tap on add-to-cart met an unchosen axis: the picker says so, once. */
  const [choiceAsked, setChoiceAsked] = useState(false);
  const pickerRef = useRef(null);
  /* THE SHOP CARD'S COVER WAITS FOR THE SHOPPER (2026-10-01). It sits at the foot of the page, yet a browser's lazy
     margin on a slow line is taller than the page, so the 68 KB cover went down the line beside the hero photo on
     every ad landing. The picture is asked for only when the card is within a screen of the viewport. */
  /* THE COLOUR CHIPS WAIT FOR THE HERO (2026-10-01). Six chips, the shop's logo and a badge went down a 4G line beside
     the first photograph and held it back by two seconds. They are asked for once the hero has arrived — or after
     three seconds, so a photograph that never arrives does not hide the choice. */
  const [heroReady, setHeroReady] = useState(false);
  useEffect(() => { const t = setTimeout(() => setHeroReady(true), 3000); return () => clearTimeout(t); }, []);
  const shopCardRef = useRef(null);
  const [shopCardNear, setShopCardNear] = useState(typeof IntersectionObserver === 'undefined');
  useEffect(() => {
    const el = shopCardRef.current;
    if (!el || shopCardNear || typeof IntersectionObserver === 'undefined') return undefined;
    const obs = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setShopCardNear(true); obs.disconnect(); }
    }, { rootMargin: '0px' });
    obs.observe(el);
    return () => obs.disconnect();
  });
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [qty, setQty] = useState(1);
  const [colorModalOpen, setColorModalOpen] = useState(false);
  /* WHICH DOOR OPENED THE COLOUR SHEET — 'add' or 'buy' — so its button finishes what the tap started: a shopper who
     pressed "Buy now" and was asked for a colour goes to the checkout from the sheet, not into the cart drawer. */
  const [choiceMode, setChoiceMode] = useState('add');
  const [liked, setLiked] = useState(false);
  const [likeBusy, setLikeBusy] = useState(false);
  const [wishBusy, setWishBusy] = useState(false);
  const [wishlisted, setWishlisted] = useState(false);
  const [reviewsModalOpen, setReviewsModalOpen] = useState(false);
  const [reviewSummary, setReviewSummary] = useState({ count: 0, average: 0 });
  const [salon, setSalon] = useState(null);
  /** The slug this page's own reading of the shop last answered for — or failed for. */
  const [shopSettledFor, setShopSettledFor] = useState(null);
  /** A product view not yet told to the pixel: it waits for the shop's campaigns (see below). */
  const owedViewRef = useRef(null);
  const currency = (salon?.currency) || ctxCurrency;
  const [freeDeliveryPromo, setFreeDeliveryPromo] = useState(null);
  const [activeTab, setActiveTab] = useState('description');
  const [justAdded, setJustAdded] = useState(false);
  /* The shelf's whole stock is already in the cart, so the press added
   * nothing: the number to say instead of "added" (UX-SHOP-15). */
  const [capNote, setCapNote] = useState(null);
  // After a WhatsApp tap inside the app's browser: the page still showing a moment later means the app did not open.
  const [waHintShown, setWaHintShown] = useState(false);
  const [qtyBump, setQtyBump] = useState(null);
  const [flyClone, setFlyClone] = useState(null);
  const [cartBump, setCartBump] = useState(false);
  const heroRef = useRef(null);
  const reviewsSectionRef = useRef(null);
  const [businessRating, setBusinessRating] = useState(EMPTY_BUSINESS_RATING);
  const addedTimerRef = useRef(null);
  const drawerTimerRef = useRef(null);
  /** The tab buttons, so the arrow keys can move focus between them. */
  const tabRefs = useRef({});
  const flyCleanupTimers = useRef([]);
  const prevCartCountRef = useRef(0);

  useEffect(() => {
    if (!salonSlug) return undefined;
    let cancelled = false;
    getSalonBySlug(salonSlug).then((s) => {
      if (cancelled) return;
      const salonObj = s?.salon ?? s;
      setSalon(salonObj);
      setShopSettledFor(salonSlug);
      if (salonObj?.id && productId) {
        trackStoreEvent({
          salonId: salonObj.id,
          eventType: 'product_open',
          payload: { productId },
        });
      }
    }).catch(() => {
      // A shop that did not load prices nothing: the view is told at the price the page then shows.
      if (!cancelled) setShopSettledFor(salonSlug);
    });
    return () => { cancelled = true; };
  }, [salonSlug, productId]);

  /* THE PRODUCT VIEW, TOLD AT THE PRICE THE PAGE SHOWS.
   *
   * Seen live on 2026-09-17, SUMMER15 running on DK Store: the GANT watch's page showed 976.65 ₪
   * and its ViewContent said 1149 — the view was valued the moment the product arrived, before the
   * shop's campaigns, at the shelf price. The product's arrival still owes the view (once per
   * page view, below); it is told here once this page's reading of the shop has settled, priced by
   * the price row's own rule and campaigns (`effectiveVariantPrice`), for the variant the page
   * opened on. The pixel could not send it sooner anyway: it waits for the same shop answer. */
  useEffect(() => {
    const view = owedViewRef.current;
    if (!view || view.product !== rawProduct || shopSettledFor !== view.shop) return;
    owedViewRef.current = null;
    const { price } = effectiveVariantPrice(view.variant, view.product, salon?.campaigns);
    // No price on the page (ask us, no price above 0): no value either — a ViewContent of 0 is a claim.
    trackViewContent({ productId: view.productId, value: hidesItsPrice(view.product) ? null : price, shop: view.shop });
  }, [rawProduct, salon, shopSettledFor]);

  useEffect(() => {
    if (salon?.currency) setSalonCurrency(salon.currency);
  }, [salon?.currency, setSalonCurrency]);

  // The page background the owner chose, published for this page too.
  //
  // Until now the product page hardcoded white and the shop page was tinted,
  // so a shopper walked from the owner's chosen colour onto a white sheet and
  // the reviews block below sat on a ground that no setting could reach. The
  // reviews surfaces read `--shop-tint` / `--shop-text` / `--shop-glass-*`
  // (see `reviewsTheme.module.css`) and those tokens are only correct while
  // the mode and the cover-derived tint are actually published — `useTintVars`
  // removes them again on unmount, so nothing leaks onto the rest of the
  // storefront. Identical derivation to `Pages/Shop/index.js`: one transform
  // (`pageTintForMode`) on one derived tint, never a second rendering path.
  const productBgMode = usePageBackgroundMode(salon?.shopBackgroundMode);
  const derivedShopTint = useCoverTint(resolveShopCoverUrl(salon));
  const productPageTint = useMemo(
    () => pageTintForMode(derivedShopTint, productBgMode),
    [derivedShopTint, productBgMode],
  );
  useTintVars(productPageTint);

  // What a review row points at. On this page every review is about THIS
  // product, so the item and the link are both known here — the review rows
  // carry only `productId`, and the public list endpoint does not embed the
  // product's name or photo (noted as a server gap). Passing them down keeps
  // the card honest rather than inventing them inside it.
  const reviewProduct = useMemo(() => (product ? {
    id: product.id,
    name: product.name,
    imageUrl: (product.images || []).find((i) => i?.isPrimary)?.url
      || (product.images || [])[0]?.url
      || null,
  } : null), [product]);
  const reviewProductHref = product ? productPath(getBasePath(salonSlug), product) : null;


  /* NO CLIENT-SIDE CORRECTION OF THE ADDRESS BAR, DELIBERATELY.
   *
   * An earlier cut of this round re-navigated here when the param was not the
   * canonical spelling — a bare uuid from an older link, or a slug minted
   * before a rename. It was cosmetic (the SERVER already answers both with a
   * 301, which is what Googlebot and every shared link get) and it was not
   * free: a `replace` while the page is live resets the variant the shopper
   * picked, and 28 ProductDetail specs went red showing the default variant's
   * price instead of the chosen one.
   *
   * That is the same fault this round came to fix, one level down — a
   * single-page app moving its own URL under a rendered page. The address bar
   * is already right for every link the app itself builds, because they are
   * all built by `productPath`.
   */

  useEffect(() => {
    if (!salonSlug) return undefined;
    let cancelled = false;
    fetchShopPromotions(salonSlug).then((data) => {
      if (!cancelled) setFreeDeliveryPromo(data?.freeDelivery ?? null);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [salonSlug]);

  useEffect(() => () => {
    flyCleanupTimers.current.forEach((id) => clearTimeout(id));
    flyCleanupTimers.current = [];
    if (addedTimerRef.current) clearTimeout(addedTimerRef.current);
    if (drawerTimerRef.current) clearTimeout(drawerTimerRef.current);
  }, []);
  useEffect(() => {
    if (!productId) return undefined;
    let cancelled = false;
    getProductLikeState(productId).then((r) => {
      if (cancelled) return;
      setLiked(!!r?.liked);
    });
    return () => { cancelled = true; };
  }, [productId]);

  useEffect(() => {
    if (!productId) return undefined;
    let cancelled = false;
    getProductReviewSummary(productId).then((s) => {
      if (!cancelled) setReviewSummary(s);
    });
    return () => { cancelled = true; };
  }, [productId]);

  /* THE BUSINESS'S OWN RATING, for the shop card at the foot of this block.
   *
   * Measured on the reference: a product page carries TWO ratings and they
   * answer two different questions — the PRODUCT's, under its name, and the
   * SHOP's, beside the shop's name. We had only the first, so the card that
   * says "All products" said nothing about how the business itself is rated.
   *
   * Keyed on the slug, which is what this route holds; the door takes either.
   * A failure answers a zero rating and the card simply shows no number. */
  useEffect(() => {
    if (!salonSlug) return undefined;
    let cancelled = false;
    Promise.resolve(getBusinessRating(salonSlug)).then((r) => {
      if (!cancelled) setBusinessRating(normaliseBusinessRating(r));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [salonSlug]);

  const handleToggleLike = async () => {
    if (likeBusy) return;
    // Liking requires an account. For guests, send them to login and bring them
    // right back to this product (don't fire the API → it 401s and the global
    // handler would bounce them off the page).
    const isLoggedIn = !!(localStorage.getItem('authToken') || localStorage.getItem('accessToken'));
    if (!isLoggedIn) {
      const back = window.location.pathname + window.location.search;
      navigate(`/login?redirect=${encodeURIComponent(back)}`);
      return;
    }
    setLikeBusy(true);
    const wasLiked = liked;
    setLiked(!wasLiked);
    try {
      const r = await (wasLiked ? unlikeProduct(productId) : likeProduct(productId));
      setLiked(!!r.liked);
    } catch (err) {
      setLiked(wasLiked);
      if (err?.response?.status === 401) {
        const back = window.location.pathname + window.location.search;
        navigate(`/login?redirect=${encodeURIComponent(back)}`);
      }
    } finally {
      setLikeBusy(false);
    }
  };

  const handleShare = async () => {
    if (!product) return;
    const url = window.location.href;
    const title = product.name;
    const text = t('productDetail.shareText', { defaultValue: 'Check out this product!' });
    try {
      if (navigator.share) {
        await navigator.share({ title, text, url });
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      }
    } catch {/* user canceled */}
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setChosenAxes(new Set());
    setLinkChose(false);
    setChoiceAsked(false);
    // No slug yet: the host lookup is still in flight, so this stays on the
    // loading screen. `fetchPublicProduct` returns null without a slug, which
    // this page reads as "no such product" — a 404 for an unfinished request.
    if (!salonSlug) return undefined;
    const linkedVariantId = variantIdInLink(openedSearchRef.current);
    fetchPublicProduct(salonSlug, productId)
      .then((p) => {
        if (cancelled) return;
        if (!p) { setNotFound(true); return; }
        /* AN AD'S COLOUR OPENS THAT COLOUR — see below; the view is of the variant the page opens on. */
        const linked = variantOpenedByLink(p, linkedVariantId);
        const first = linked ?? p.variants?.[0];
        // The same moment owes the Meta Pixel a product view, in the catalogue feed's ids (a product
        // is the item group of its variants), naming the shop so it reaches that shop's pixel and no
        // other. It is told at the price the page shows once the shop's campaigns are known (above).
        owedViewRef.current = { productId: p.id ?? productId, product: p, variant: first, shop: salonSlug };
        setProduct(p);
        addRecentlyViewed(salonSlug, p);
        /* AN OPEN, RECORDED AGAINST US RATHER THAN AGAINST AN ANALYTICS
         * VENDOR. The `trackStoreEvent('product_open')` above goes to
         * `/analytics/events`, which this product cannot read back — nothing
         * in this codebase could answer "what do shoppers open", which is why
         * the "hot" label needed a measurement of its own.
         *
         * Fired HERE and not on mount, so a URL that 404s is not an open. Once
         * per product per session on this side; once per visitor per day on
         * the server, which is the rule that actually counts. */
        recordProductOpen(salonSlug, p.id ?? productId);
        /* AN AD'S COLOUR OPENS THAT COLOUR. The catalogue feed links each variant of a product with
         * more than one to `?variant=<id>`; the page opens on it — its photos, price, stock and picks
         * all follow from the three picks below — when the pickers can reach it
         * (utils/variantLink). Anything else opens on the first variant, as always. The address is
         * left as it is, then and when the shopper picks another. The variant is `first`, above. */
        if (first) {
          setPickedColor(first.color || null);
          setPickedSize(first.size || null);
          setPickedStyle(first.style || null);
        }
        /* THE AD'S COLOUR IS THE SHOPPER'S CHOICE — ON THE OPEN THAT CAME FROM THE AD. They tapped an ad
         * for this very option, so it opens chosen and one tap adds it. The address keeps `?variant=` after
         * they pick another, and comes back after login, Back or a reload: those opens show the link's
         * colour but choose nothing. A page opened any other way — or by a link that names nothing the
         * pickers reach — waits for the shopper to choose, as every page does. */
        if (linked && linkChoosesOnThisOpen(salonSlug, p.id ?? productId, linked.id)) setLinkChose(true);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 404) setNotFound(true);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [salonSlug, productId]);

  // Color is the primary axis — every color the product offers is always
  // shown, regardless of the current size/type pick. Sizes are filtered to
  // what exists for the picked color; types are filtered to what exists for
  // the picked (color, size). This avoids the "everything looks disabled"
  // trap when the previous color's size/type isn't valid for the new color.
  // In the payload's order: the server reads the options by their display order (round C: S2, W5).
  const colorOptions = useMemo(
    () => uniqOf(product?.variants, 'color'),
    [product],
  );
  // An option value as this reader reads it (utils/shopperText): the product's own translation → the shop's choice
  // library → the value as written (a preset colour in the storefront's own words). Display only — the picks, the
  // matching and the cart keep the value exactly as stored.
  const valueLabel = (slot, v) => optionValueShown(product, slot, v, i18n.language, t);
  const colorLabel = (c) => valueLabel('color', c);
  // An axis heading: the choice's own name in this language (`name ?? label`) → the label the shop gave it → the
  // storefront's own generic word.
  const axisName = (slot, _slotIndex, fallbackKey, fallbackDefault) => (
    axisNameIn(product, slot, i18n.language) || t(fallbackKey, { defaultValue: fallbackDefault })
  );
  // The colour slot is positional and may actually be a non-colour custom
  // choice (e.g. a NUMBER "Carat"/"قيراط"). Only paint a colour swatch when the
  // axis is genuinely a colour; legacy products with no choice metadata default
  // to the swatch to preserve existing behaviour.
  const axisIsColorSwatch = (slotIndex) => {
    const vt = product?.choices?.[slotIndex]?.valueType;
    return vt ? vt === 'COLOR' : true;
  };
  const sizeOptions = useMemo(() => {
    if (!product?.variants?.length) return [];
    const pool = pickedColor
      ? product.variants.filter((v) => v.color === pickedColor)
      : product.variants;
    return uniqOf(pool, 'size');
  }, [product, pickedColor]);
  const styleOptions = useMemo(() => {
    if (!product?.variants?.length) return [];
    const pool = product.variants.filter((v) =>
      (!pickedColor || v.color === pickedColor) &&
      (!pickedSize  || v.size  === pickedSize),
    );
    return uniqOf(pool, 'style');
  }, [product, pickedColor, pickedSize]);

  // Cascade-correct the picked values whenever a parent axis change leaves
  // the child pick invalid (e.g. picking a color that doesn't have the
  // currently-picked size). Auto-selects the sole option when only one is
  // possible, so the matched variant resolves cleanly.
  useEffect(() => {
    if (!product?.variants?.length) return;
    if (sizeOptions.length === 0) {
      if (pickedSize) setPickedSize(null);
    } else if (!sizeOptions.includes(pickedSize)) {
      setPickedSize(sizeOptions[0]);
      // The page moved the size, not the shopper: it is theirs to choose again.
      setChosenAxes((prev) => (prev.has('size') ? withoutAxis(prev, 'size') : prev));
    }
  }, [product, sizeOptions, pickedSize]);
  useEffect(() => {
    if (!product?.variants?.length) return;
    if (styleOptions.length === 0) {
      if (pickedStyle) setPickedStyle(null);
    } else if (!styleOptions.includes(pickedStyle)) {
      setPickedStyle(styleOptions[0]);
      setChosenAxes((prev) => (prev.has('style') ? withoutAxis(prev, 'style') : prev));
    }
  }, [product, styleOptions, pickedStyle]);

  const matchedVariant = useMemo(() => {
    if (!product?.variants?.length) return null;
    return product.variants.find((v) =>
      (!pickedColor || v.color === pickedColor) &&
      (!pickedSize  || v.size  === pickedSize)  &&
      (!pickedStyle || v.style === pickedStyle),
    ) ?? null;
  }, [product, pickedColor, pickedSize, pickedStyle]);

  const selectedVariant = matchedVariant ?? product?.variants?.[0] ?? null;
  const isUnavailableCombo = !!product?.variants?.length && !matchedVariant;

  // Per-option stock state inside each axis. Colors are out-of-stock only
  // when every variant of that color has zero stock (and inventory is
  // tracked). Sizes/types are evaluated against the parent axes already
  // picked. Options never marked "unavailable" here — the cascade above
  // ensures only existing combinations are presented.
  const optionAvailability = useMemo(() => {
    if (!product?.variants?.length) return { color: {}, size: {}, style: {} };
    const tracks = !!product.trackInventory;
    const inStock = (variants) => !tracks || variants.some((v) => (v.stockOnHand ?? 0) > 0);
    const out = { color: {}, size: {}, style: {} };
    for (const c of colorOptions) {
      const matching = product.variants.filter((v) => v.color === c);
      out.color[c] = inStock(matching) ? 'available' : 'out_of_stock';
    }
    for (const s of sizeOptions) {
      const matching = product.variants.filter((v) =>
        (!pickedColor || v.color === pickedColor) && v.size === s,
      );
      out.size[s] = inStock(matching) ? 'available' : 'out_of_stock';
    }
    for (const st of styleOptions) {
      const matching = product.variants.filter((v) =>
        (!pickedColor || v.color === pickedColor) &&
        (!pickedSize  || v.size  === pickedSize)  &&
        v.style === st,
      );
      out.style[st] = inStock(matching) ? 'available' : 'out_of_stock';
    }
    return out;
  }, [product, colorOptions, sizeOptions, styleOptions, pickedColor, pickedSize]);

  /* THE AXES STILL WAITING FOR THE SHOPPER, in the order the picker draws them.
   *
   * An axis asks for a choice when it offers more than one option a shopper
   * can actually buy and nobody has tapped it yet. One option on the shelf —
   * a single variant, or two platings with one sold out — is nothing to
   * choose, and stays a one-tap add. */
  const pendingAxes = [['color', colorOptions], ['size', sizeOptions], ['style', styleOptions]]
    .filter(([axis, options]) => !linkChose && !chosenAxes.has(axis)
      && options.filter((o) => (optionAvailability[axis][o] || 'available') === 'available').length > 1)
    .map(([axis]) => axis);
  const isPending = (axis) => pendingAxes.includes(axis);
  /** Whether the value on an axis is the shopper's own: tapped, or chosen by the ad link they came by. */
  const shopperChose = (axis) => linkChose || chosenAxes.has(axis);
  /** A tap in the picker (or the full-screen viewer's) is the shopper's choice — of that axis only: it ends
   * the link's choice, and the other axes stay chosen only if the shopper tapped them before. */
  const choose = (axis, value) => {
    if (axis === 'color') setPickedColor(value);
    else if (axis === 'size') setPickedSize(value);
    else setPickedStyle(value);
    setLinkChose(false);
    setChosenAxes((prev) => (prev.has(axis) ? prev : new Set([...prev, axis])));
  };
  /* THE COLOUR SHEET'S PICKS. A pick there is not a choice until its own add (round 13), but it moves the
   * page off the link's variant, so it ends the link's choice as a tap in the page's picker does. */
  const sheetPicks = useMemo(() => ({
    color: (value) => { setLinkChose(false); setPickedColor(value); },
    size: (value) => { setLinkChose(false); setPickedSize(value); },
    style: (value) => { setLinkChose(false); setPickedStyle(value); },
  }), []);

  /* EVERY PHOTO THE PRODUCT HAS, not only the chosen variation's one.
   *
   * This used to be `selectedVariant?.images ?? []`. A catalogue imported from
   * WooCommerce keeps its gallery on the PRODUCT and gives each variation the
   * single swatch shot, so that read showed 1 of 42 on the merchant's
   * jewellery sets and put 26.6% of every photo we downloaded out of reach.
   * The rule (chosen variation first, then the product's own, deduplicated)
   * lives in `utils/productGallery.js` so the three surfaces that answer this
   * question cannot drift apart again. */
  /* …AND A VALUE WITH NO PICTURE OF ITS OWN KEEPS THE PRODUCT'S MAIN PICTURE IN FRONT (Swe Sosetta run 3, panel S5):
   * the makrouta's Nutella led with the owner's blurry mixed chat photo because that is all its card had. Values that
   * share one picture (utils/optionPictures) are drawn as words, and choosing one swaps nothing. */
  const sharedColour = useMemo(() => valuesWithoutOwnPicture(product, 'color'), [product]);
  const sharedStyle = useMemo(() => valuesWithoutOwnPicture(product, 'style'), [product]);
  /* One widget per choice (round C: S2): tiles when any value on the slot has a picture of its own, else pills. */
  const colourWidget = choiceWidget(product, 'color', colorOptions);
  const styleWidget = choiceWidget(product, 'style', styleOptions);
  const leadsWithItsOwn = !!selectedVariant
    && !(selectedVariant.color ? sharedColour.has(selectedVariant.color) : (selectedVariant.style && sharedStyle.has(selectedVariant.style)));
  const images = useMemo(
    () => galleryImages(product, leadsWithItsOwn ? selectedVariant : null),
    [product, selectedVariant, leadsWithItsOwn],
  );
  // ONE reading of the price, for the price row, the sale card and the cart.
  // `price` is what the shopper is shown (campaigns counted); `listPrice` is
  // the shelf price the cart stores — the checkout takes the discount itself.
  const { price, compareAt, discounted, pct: discountPct } = effectiveVariantPrice(
    matchedVariant ?? selectedVariant,
    product,
    salon?.campaigns,
  );
  const campaignPricing = campaignLinePricing(matchedVariant ?? selectedVariant, product, salon?.campaigns);
  /* «من ٦٥ ₪» UNTIL IT IS PICKED, AND EACH TILE ITS PRICE (D1-24, walk 1 of 2026-10-28): the makrouta's first row said
   * 65 while its Nutella cost 70. While a choice the shopper has not made changes the price, the row says "from" the
   * cheapest row still open to them, read as the shop's cards read it; the values of a slot that cost differently each
   * carry their own (utils/choicePrices). */
  const priceFrom = fromPrice(product, pendingAxes, salon?.campaigns, { color: pickedColor, size: pickedSize, style: pickedStyle });
  const choicePrices = useMemo(
    () => tilePrices(product, { color: pickedColor, size: pickedSize }, salon?.campaigns, {
      size: linkChose || chosenAxes.has('size') ? pickedSize : null,
      style: linkChose || chosenAxes.has('style') ? pickedStyle : null,
    }),
    [product, pickedColor, pickedSize, pickedStyle, linkChose, chosenAxes, salon?.campaigns],
  );
  const choicePriceIds = useId();
  const fromWord = t('shop.priceFrom', { defaultValue: 'from' });
  /* THE RAIL GOES BACK TO THE FRONT WHEN THE SHOPPER CHANGES THEIR MIND.
   *
   * The gallery leads with the chosen variation, so an index left over from
   * the previous colour points at an unrelated photo — and on a 42-image rail
   * it can point past the end of a shorter one. Keyed on the variant's id
   * rather than on the array, which is a new object every render. */
  const selectedVariantId = selectedVariant?.id ?? null;
  /**
   * THE SPECIFICATION SHEET, AND WHETHER THERE IS ONE.
   *
   * Built from what this shop actually holds for THIS row — the SKU, the
   * brand, the category, the axis they picked, the weight and the box. An
   * empty list is the signal to leave the tab out entirely: a tab that opens
   * on nothing is a worse answer than a tab that is not there.
   */
  const pendingKey = pendingAxes.join(',');
  const specRows = useMemo(
    () => buildProductSpecs({
      product,
      variant: selectedVariant,
      t,
      valueLabel: (slot, v) => optionValueShown(product, slot, v, i18n.language, t),
      // Only what the shopper chose (or what there is no choosing of) — never the first variant's flavour (S3).
      shownSlot: (slot) => !pendingKey.split(',').includes(slot),
    }),
    [product, selectedVariant, t, i18n.language, pendingKey],
  );

  /**
   * THE TWO PROMISES, WITH THE ROW HAVING THE LAST WORD.
   *
   * The server already walked product → category → business and published one
   * settled answer. Only the page knows which row the shopper picked, so it
   * applies exactly ONE more link — and applies it WHOLE. A merge would let a
   * clearance row that just said "no returns" keep the shop's 14-day window
   * beside it, which is two promises on one screen.
   */
  const rowReturnPolicy = selectedVariant?.returnPolicy ?? null;
  const rowWarranty = selectedVariant?.warranty ?? null;
  // The shelf is named again as a LAST resort. The server already folded it in
  // — `returnPolicy` on the payload has been the resolved answer since before
  // this round — but a client that can still answer from a payload it holds is
  // a client that does not go blank against an older server. It agrees with
  // the server in every case where the server said anything at all.
  const returnPolicy = rowReturnPolicy ?? product?.returnPolicy ?? product?.category?.returnPolicy ?? null;
  const warranty = rowWarranty ?? product?.warranty ?? product?.category?.warranty ?? null;

  /**
   * OURS WHEN THE SHOP WROTE NOTHING.
   *
   * The server answers `level: 'platform'` when no row in the shop states a
   * promise. That is not "nothing to show" — it is "use yours", and the words
   * are here rather than on the server because only this side knows which
   * language the shopper reads. A ROW that overrides is still the row's: the
   * level below only speaks when the row above it is silent.
   */
  const shownWarranty = useMemo(
    // The generated sentence rides on the payload as a KEY and its number; the
    // words are rendered here, from this shopper's own locale file. A row that
    // overrides carries its own answer and its own sentence is not the
    // product's, so the generated one is dropped with it.
    () => warrantyForDisplay(warranty, rowWarranty ? null : product?.warrantySentence, t),
    [warranty, rowWarranty, product?.warrantySentence, t],
  );
  // A food shop's default names what the right to cancel does not cover (store-wide polish, Task 4). The answer
  // rides on the shop payload this page already reads; only `true` is a yes.
  const sellsFood = salon?.sellsFood === true;
  // WHICH default is right depends on the shop, so none is drawn until this page's reading of the shop has landed
  // (fix round 1): today's sentence first and the food one a moment later would show a food shop's shopper the
  // wrong one. A shop's OWN statement does not wait, and a reading that failed leaves no default.
  const shopKnown = shopSettledFor === salonSlug && salon != null;
  const shownReturns = useMemo(() => {
    const shown = returnsForDisplay(returnPolicy, rowReturnPolicy ? 'variant' : product?.returnPolicyLevel, t, { sellsFood });
    return shown && shown.isDefault && !shopKnown ? null : shown;
  }, [returnPolicy, rowReturnPolicy, product?.returnPolicyLevel, t, sellsFood, shopKnown]);

  /**
   * A TAB WITH NO CONTENT MUST NOT EXIST.
   *
   * The owner, about the warranty: "if there's no warranty, don't have this
   * tab at all in the product." It is true of every tab — a description tab
   * that opens on "No description for this product yet." is the same defect in
   * different words, and his own site already does this: 1 of the 25 products
   * I sampled serves three tabs instead of four, because it has no description.
   *
   * Each entry names the predicate its panel renders on, so the tab and the
   * panel can never disagree about whether there is anything in there.
   */
  const hasDescription = !!(product?.descriptionHtml || (product?.description && String(product.description).trim()));
  const hasReturns = !!shownReturns;
  // Shipping is OUR content, not the merchant's: every sellable row can be
  // delivered or collected, so the tab is empty only when there is no row to
  // ship and no returns promise to state.
  const hasDelivery = !!selectedVariant || hasReturns;

  const infoTabs = useMemo(() => [
    ...(hasDescription
      ? [{ key: 'description', label: t('productDetail.description', { defaultValue: 'Description' }) }]
      : []),
    ...(specRows.length
      ? [{ key: 'specs', label: t('productDetail.specsTab', { defaultValue: 'Specifications' }) }]
      : []),
    // The merchant's own tab is "משלוחים והחזרות" — shipping AND returns, one
    // heading. The returns promise used to be filed under the description.
    ...(hasDelivery
      ? [{ key: 'delivery', label: t('productDetail.deliveryTab', { defaultValue: 'Delivery information' }) }]
      : []),
    // His fourth tab, and the one he was asked about by name.
    ...(shownWarranty
      ? [{ key: 'warranty', label: t('productDetail.warrantyTab', { defaultValue: 'Warranty' }) }]
      : []),
  ], [hasDescription, specRows.length, hasDelivery, shownWarranty, t]);

  // A tab that stops existing — the shopper picks a row with no SKU, say, or a
  // final-sale row with no warranty — must not leave the page showing an empty
  // panel and no selected tab. It falls back to the FIRST tab that exists,
  // which is not always the description: a product with none has no such tab.
  useEffect(() => {
    if (!infoTabs.length) return;
    if (!infoTabs.some((tab) => tab.key === activeTab)) setActiveTab(infoTabs[0].key);
  }, [infoTabs, activeTab]);

  /**
   * ARROW KEYS ALONG THE ROW, IN THE DIRECTION THE ROW IS ACTUALLY DRAWN.
   *
   * The pattern says Left/Right move between tabs; in Hebrew and Arabic the
   * NEXT tab is the one to the LEFT, because the row is laid out
   * right-to-left. Getting this wrong is not a preference — the whole
   * catalogue this was built for is Hebrew.
   */
  const onTabKeyDown = (e) => {
    const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (!keys.includes(e.key)) return;
    e.preventDefault();
    const at = infoTabs.findIndex((tab) => tab.key === activeTab);
    const forward = isRtl ? 'ArrowLeft' : 'ArrowRight';
    let next = at;
    if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = infoTabs.length - 1;
    else if (e.key === forward) next = (at + 1) % infoTabs.length;
    else next = (at - 1 + infoTabs.length) % infoTabs.length;
    const key = infoTabs[next].key;
    setActiveTab(key);
    const el = tabRefs.current[key];
    if (el && el.focus) el.focus();
  };

  /* THE RESET IS FOR A CHANGE OF MIND, NOT FOR THE PAGE ARRIVING.
   *
   * This used to fire on every value `selectedVariantId` took, and the first
   * one it takes is `null` → the id, one commit AFTER the photographs are on
   * the screen: the product's own `images` render as soon as the payload
   * lands, but the variant is matched from the picked colour/size/style,
   * which settles on the following tick. A shopper who reached the gallery in
   * that window — and with an unresized 480KB photograph still decoding, that
   * window is real — had their first move silently undone.
   *
   * Nothing is lost by skipping it: the rail is already at 0 when the page
   * arrives. What the reset is FOR is the shopper changing colour, where an
   * index left over from the previous variation points at an unrelated photo
   * and, on the merchant's 42-image jewellery sets, can point past the end of
   * a shorter rail. That case still fires — see
   * `theWholeGalleryIsReachable.test.js`, which asserts it.
   */
  const lastVariantIdRef = useRef(undefined);
  /* …and a change of variant on the same product is a step of a visit from an ad (utils/shopJourney.js),
   * with the variant before it. A new product is a new page, not a choice. */
  const lastVariantOfRef = useRef(null);
  useEffect(() => {
    const before = lastVariantOfRef.current;
    lastVariantOfRef.current = product && selectedVariantId ? { productId: product.id, variantId: selectedVariantId } : null;
    if (before && product && selectedVariantId && before.productId === product.id && before.variantId !== selectedVariantId) {
      journeyVariant({ productId: product.id, variantId: selectedVariantId, fromVariantId: before.variantId });
    }
  }, [product, selectedVariantId]);
  useEffect(() => {
    const previous = lastVariantIdRef.current;
    lastVariantIdRef.current = selectedVariantId;
    // `undefined` is the first run, `null` is the page settling on a variation
    // it had not matched yet. Neither is a shopper changing their mind, and
    // neither leaves an index pointing anywhere it should not.
    if (previous === undefined || previous === null) return;
    setGalleryIndex(0);
  }, [selectedVariantId]);

  /* ── THE FOUR INPUTS A DESKTOP HAS, ON THE INDEX THIS PAGE ALREADY OWNS ──
   *
   * The owner: "ours can't scroll right and left on the pic itself or in the
   * strip under it, in PC!" — and he is right, but not because an `overflow`
   * was missing. `ProductCarousel` is a translate-X track: its `.wrap` is
   * `position:absolute; inset:0; overflow:hidden` and the `.track` inside is
   * absolutely positioned, so `scrollWidth === clientWidth` and there is
   * nothing a browser could scroll even if we asked it to. What moves that
   * track is `onTouchStart/Move/End` — three handlers a mouse never fires. On
   * a phone the swipe carries it. On a desktop the component had no pointer,
   * no wheel and no keyboard path at all, and the only navigation left was a
   * thumbnail strip 650px wide inside a 514px column.
   *
   * So the answer is controls, not overflow. The carousel is already a
   * CONTROLLED component — this page owns `galleryIndex` and hands it down —
   * which means every desktop gesture can be expressed here, against that one
   * number, and `ProductCarousel` itself is not touched. That matters: the
   * same component draws every card in the shop grid, and a new gesture added
   * inside it would land on all of them.
   */
  const galleryCount = images.length;
  /* THE FRAME IS THE PICTURE ITSELF, BLURRED (Swe Sosetta run 4, round B: S4). A square picture in the 4:5 `contain`
   * stage left two bands of the page's plate above and below it. Round A painted them the picture's edge colour, but
   * only when 90% of the edge was one even colour — a cream studio backdrop reached 65–70%, so it never fired. Now the
   * stage's ground is a blurred, enlarged copy of the shown picture, for every picture and any background; a video
   * keeps the plate. */
  const shownPictureUrl = images[galleryIndex]?.url ?? images[0]?.url ?? null;
  /* SAY WHOSE PICTURE IT IS (run 4, round B: S6). A chosen flavour with no picture of its own shows the product's
   * pictures — Nutella showed the Kinder one. The shown picture's own option is named on it. */
  /* «بالصورة: كيندر» / «בתמונה: קינדר» / "Pictured: Kinder" (Swe Sosetta run 5, round C: S3): the value alone. The
   * axis-and-value template read «الصورة لنكهة عادية», «الصورة لنوع مشكل» and «בתמונה: סוג מבחר טעמים». */
  const pictureSlot = selectedVariant?.color ? 'color' : (selectedVariant?.style ? 'style' : null);
  const chosenSharedValue = pictureSlot && !leadsWithItsOwn && !isPending(pictureSlot) ? selectedVariant[pictureSlot] : null;
  const shownPictureOwner = chosenSharedValue ? pictureOwner(product, pictureSlot, shownPictureUrl) : null;
  const pictureOfCaption = shownPictureOwner && shownPictureOwner !== chosenSharedValue
    ? t('productDetail.pictureOf', {
      defaultValue: 'Pictured: {{value}}',
      value: optionValueShown(product, pictureSlot, shownPictureOwner, i18n.language, t),
    }).replace(/\s{2,}/g, ' ').trim()
    : null;
  /* EVERY picture gets the blurred frame, whatever its format (fix round 2: production serves every picture as a
   * `.w1200.webp` rendition, so guessing see-through from the format turned the frame off everywhere). A cut-out never
   * shows its own ghost because the carousel draws each picture in a box of its own shape on the plain surface
   * (ProductCarousel `framed`): the blur is only in the bands around it. */
  /* …except a picture with no shape of its own (an SVG with no intrinsic size, a failed load: the carousel says so):
   * it is drawn on the plain frame, with no blurred copy behind it (fix round 3). */
  const [unsizedPictures, setUnsizedPictures] = useState(() => new Set());
  const notePictureUnsized = useCallback((url) => {
    setUnsizedPictures((prev) => (prev.has(url) ? prev : new Set([...prev, url])));
  }, []);
  const stageBackdropUrl = shownPictureUrl && !isVideoUrl(shownPictureUrl) && !unsizedPictures.has(shownPictureUrl)
    ? renditionUrl(shownPictureUrl, 480)
    : null;
  const stageRef = useRef(null);
  const railRef = useRef(null);
  /** Clamped, never wrapped: a shopper at the last photo of eight has reached
   *  the end of the photographs, and silently returning them to the first is
   *  how you lose your place in a rail you are working through. */
  const goToImage = (next, action = 'arrow') => {
    if (galleryCount <= 1) return;
    const target = Math.min(galleryCount - 1, Math.max(0, typeof next === 'function' ? next(galleryIndex) : next));
    if (target !== galleryIndex) galleryStep('product', action, target);
    setGalleryIndex((cur) => {
      const moved = typeof next === 'function' ? next(cur) : next;
      return Math.min(galleryCount - 1, Math.max(0, moved));
    });
  };
  const stepImage = (delta, action) => goToImage((cur) => cur + delta, action);
  /** A move through the photos, told to the journey of a visit from an ad (utils/shopJourney.js). */
  const galleryStep = (gallery, action, photoIndex) => journeyGallery(gallery, {
    action, photoIndex, photoCount: galleryCount, productId: product?.id ?? productId, variantId: selectedVariantId,
  });

  /* THE ACTIVE THUMBNAIL COMES INTO VIEW BY ITSELF.
   *
   * The rail stands vertically beside the stage on a desktop and holds about
   * six of them; on the merchant's 42-image jewellery sets the thumbnail for
   * the photo now showing is usually off the end of it. `nearest` rather than
   * `center` so a rail that already shows the thumbnail does not lurch. */
  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return;
    const active = rail.querySelector('[aria-selected="true"]');
    if (active && active.scrollIntoView) {
      active.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }
  }, [galleryIndex, galleryCount]);

  /* A SIDEWAYS WHEEL IS THE ONE THE OWNER MEANT.
   *
   * A trackpad's horizontal flick and a mouse's shift+wheel are the two ways a
   * desktop says "sideways", and both arrive as `deltaX`. A STRAIGHT vertical
   * wheel is deliberately left alone and NOT preventDefault-ed, because over a
   * 600px-tall photograph that is how the customer scrolls the page — turning
   * it into image navigation would trap them on the hero.
   * `utils/forwardVerticalWheel.js` is built on the same rule.
   *
   * One flick emits a burst of a dozen events, so a step is rate-limited to
   * one per 260ms — otherwise a single gesture runs to the end of the rail. */
  const wheelLockRef = useRef(0);
  const handleStageWheel = (e) => {
    if (galleryCount <= 1) return;
    const dx = e.shiftKey ? (e.deltaX || e.deltaY) : e.deltaX;
    if (!dx || Math.abs(dx) <= Math.abs(e.deltaY) * (e.shiftKey ? 0 : 1)) return;
    const now = Date.now();
    if (now - wheelLockRef.current < 260) { e.preventDefault(); return; }
    wheelLockRef.current = now;
    e.preventDefault();
    // The browser reports the PHYSICAL axis; reading order is ours to apply.
    // In Hebrew and Arabic the next photograph lies to the left, which is the
    // same convention the tab strip above already uses.
    stepImage(dx > 0 ? (isRtl ? -1 : 1) : (isRtl ? 1 : -1), 'swipe');
  };

  /* A MOUSE DRAG, WHICH IS THE OTHER THING "SCROLL ON THE PIC" MEANS.
   *
   * MOUSE events, not pointer events, and that is a decision rather than a
   * default. Pointer events would also cover a pen, but they would ALSO cover
   * touch, and touch is already served by `ProductCarousel`'s own swipe —
   * running both would step every phone gesture twice. Filtering pointer
   * events down to `mouse`/`pen` gets us back to exactly the mouse events, by
   * a route jsdom cannot even represent (it ships no `PointerEvent`, so a
   * test of the drag would have been a test of nothing).
   *
   * A pen and a touch tap both emit compatibility mouse events, but neither
   * emits a 60px `mousemove` with the button held: browsers suppress compat
   * mouse events for pans and swipes precisely so handlers like this one do
   * not fire twice. So the threshold is what keeps this desktop-only.
   *
   * The stage is also the button that opens the full-screen viewer, so a drag
   * that crossed the threshold has to swallow the `click` that follows it, or
   * every drag ends in a modal. */
  const dragRef = useRef({ active: false, x: 0, moved: false });
  const suppressClickRef = useRef(false);
  const DRAG_STEP_PX = 60;
  const onStageMouseDown = (e) => {
    if (galleryCount <= 1 || e.button !== 0) return;
    dragRef.current = { active: true, x: e.clientX, moved: false };
  };
  const onStageMouseMove = (e) => {
    const drag = dragRef.current;
    if (!drag.active || drag.moved) return;
    const dx = e.clientX - drag.x;
    if (Math.abs(dx) < DRAG_STEP_PX) return;
    drag.moved = true;
    suppressClickRef.current = true;
    // Pulling the photograph toward the start of the line brings the next one
    // in behind it — which is leftward in English and rightward in Hebrew.
    stepImage(dx < 0 ? (isRtl ? -1 : 1) : (isRtl ? 1 : -1), 'swipe');
  };
  const endStageDrag = () => { dragRef.current = { active: false, x: 0, moved: false }; };

  /** The gallery's own keys. `Enter` still opens the viewer (below). */
  const handleStageKeyDown = (e) => {
    if (galleryCount <= 1) return false;
    const forward = isRtl ? 'ArrowLeft' : 'ArrowRight';
    const back = isRtl ? 'ArrowRight' : 'ArrowLeft';
    if (e.key === forward) { e.preventDefault(); stepImage(1); return true; }
    if (e.key === back) { e.preventDefault(); stepImage(-1); return true; }
    if (e.key === 'Home') { e.preventDefault(); goToImage(0); return true; }
    if (e.key === 'End') { e.preventDefault(); goToImage(galleryCount - 1); return true; }
    return false;
  };

  const stock = matchedVariant?.stockOnHand ?? 0;
  const tracks = !!product?.trackInventory;
  const variantOutOfStock = !!matchedVariant && tracks && stock <= 0;
  const isOutOfStock = isUnavailableCombo || variantOutOfStock;
  const cartCount = cart?.count ?? 0;
  /* THE SAME ARM AS THE CHECKOUT (2026-10-12, the owner: "I want all these new changes of today to be under AB testing").
     The second arm's bar leads with add whatever the basket holds; the first arm's leads with "go to the basket" once
     something is in it (2026-10-07). One visitor, one checkout, from this bar to the payment. */
  const leanCheckout = useMemo(() => checkoutArm(getVisitorKey()) !== 'plain', []);
  const freeShippingThreshold = Number(freeDeliveryPromo?.threshold ?? 0);

  /* OUT OF THE APP'S BROWSER, FROM THE LANDING PAGE (2026-10-08).
   *
   * The owner, three times: "why can't we open the link still in the browser directly instead of in app of
   * facebook and ig? The dk store ads doing it! And it's better for me", then "investigate how, and to allow
   * us doing that!", then "especially the in-app browser, I want it to be opened in the browser". He is right
   * that DK's own ad does it — the "◀ Facebook" breadcrumb in his screenshot is what iOS shows when an app
   * hands a link to Safari.
   *
   * The mechanism is the one the checkout already uses (utils/openInBrowser.js): Safari's scheme on iOS,
   * Chrome's intent on Android, with the ad click carried across so the sale still credits its ad. All that
   * was missing was a door at the LANDING page rather than only at the payment step.
   *
   * IT IS A TAP, NOT AN AUTOMATIC JUMP, and that is a deliberate limit rather than a half-measure. An
   * unprompted scheme redirect on arrival is the kind of thing Meta's own policies treat as an unexpected
   * redirect, and an ad rejected for it costs more than the hand-off wins. The scheme is also swallowed often
   * enough that the checkout's version waits 1.5s and then tells the shopper where their app's own door is —
   * firing that unprompted would leave some visitors staring at a page that did nothing.
   *
   * WHAT THE NUMBERS SAY, recorded here because it argues against this feature and the owner asked for it
   * anyway, which is his call to make: over 30 days of DK's ad traffic, cut to one device class (iPhones from
   * a facebook/instagram referrer on an ad click), in-app added to cart 13 times across 265 people and real
   * Safari 0 times across 40. Forty is too small to call Safari worse, but there is no measured upside here.
   * The measured loss is the checkout's name field, and it survives the jump.
   */
  /* The door is not shown at rest: the page ASKS (Components/InAppBrowserPrompt, 2026-10-25) and leaves on a tap.
     The 2026-10-08 notes below describe the automatic jump it replaced; the mechanism is still the same one. */
  const openThisInBrowserRef = useRef(null);
  const inAppHere = useMemo(
    () => isInAppBrowser(typeof navigator !== 'undefined' ? navigator.userAgent : ''),
    [],
  );
  const phoneOsHere = useMemo(
    () => osOf(typeof navigator !== 'undefined' ? navigator.userAgent : ''),
    [],
  );
  /* GOING BY ITSELF (2026-10-08). The owner, after I showed him the tapped door: "revert them and
   * ensure opening them in the device browser." He is right that a button is not what he asked for
   * — he tapped a product in a Facebook ad strip and it simply opened in Safari.
   *
   * WHAT WE PROVED FIRST, so this is not a guess at their mechanism: dk-store.co.il does NOTHING to
   * escape. Fetched with a Facebook in-app user agent, it carries no `x-safari-`, no `intent://`, no
   * App Links (`al:`) tags, and its only redirects are WooCommerce's cart ones. Neither site has
   * App Links. So their jump comes from the AD, not the page, and nothing a site does can reproduce
   * it. This is therefore our own best effort at the same end, not a copy of theirs.
   *
   * THE THREE GUARDS THAT KEEP IT FROM BEING A TRAP:
   *   1. never when the address already carries `handoff` — that IS the far side of a jump, and
   *      jumping again is an infinite loop between the app and the browser;
   *   2. once per shop per session, remembered before the attempt, so a scheme that half-works or a
   *      remount cannot fire it twice;
   *   3. if the app swallows the scheme the page is still here, so the visible door takes over —
   *      that is why the door is kept rather than deleted. Nobody is left on a page that did
   *      nothing with no way forward.
   *
   * It is deliberately the FIRST thing to switch off if an ad is ever rejected for an unexpected
   * redirect, which is the risk I put to the owner twice before building it. */
  const openThisInBrowser = (opts = {}) => {
    if (typeof window === 'undefined') return;
    const items = (cart?.items || [])
      .filter((i) => i && i.productId && i.variantId)
      .map((i) => ({ productId: i.productId, variantId: i.variantId, quantity: i.quantity }));
    const https = handoffUrl({
      origin: window.location.origin,
      pathname: routePathname,
      items,
      adVisit: currentAdVisit(),
      visitorKey: getVisitorKey(),
    });
    if (salon?.id) {
      trackStoreEvent({
        salonId: salon.id,
        eventType: 'shop_open_in_browser',
        payload: { at: 'product', os: phoneOsHere, lines: items.length, productId: product?.id ?? null, automatic: !!opts.automatic },
      });
    }
    /* NO DOOR WHEN IT IS SWALLOWED (2026-10-10, the owner, from inside Facebook's browser: "wtf is this in the
       bottom — remove, it looks very bad"). The attempt is silent: if the app keeps the shopper, the page stays
       exactly the page — no "open in Safari" bar, no "didn't open?" line stacked over the price. The way out to
       Safari lives on the checkout's payment step only (checkout-open-in-browser), where paying needs it. */
    leaveForBrowser(openInBrowserHref(https, phoneOsHere));
    /* AND WHETHER IT WORKED (2026-10-14, the owner: "make sure we have full coverage of movements in
     * the website"). The attempt above has been recorded since the day it was built; what happened
     * next never was. Thirty days of logs therefore cannot answer the one question this feature
     * stands or falls on — whether the app swallows the scheme — and the whole case for keeping the
     * fallback door, and for the jump itself, rests on a guess.
     *
     * The same proxy the WhatsApp order has used since 2026-10-07: 1.5 s later, a page that has gone
     * hidden lost the screen to something else, and a page still in front of the shopper was
     * swallowed. `opened` is the honest word for it — nothing in a web page can see Safari open. One
     * result per jump, and the jump is already once per shop per device per day. */
    if (salon?.id) {
      setTimeout(() => {
        if (typeof document === 'undefined') return;
        trackStoreEvent({
          salonId: salon.id,
          eventType: 'shop_open_in_browser_result',
          payload: {
            automatic: !!opts.automatic,
            os: phoneOsHere,
            opened: document.visibilityState === 'hidden',
            productId: product?.id ?? null,
          },
        });
      }, HANDOVER_RESULT_MS);
    }
  };
  openThisInBrowserRef.current = openThisInBrowser;
  const AUTO_LEAVE_KEY = `stylon:inapp:left:${salonSlug}`;
  /* THE PAGE ASKS; IT NO LONGER JUMPS (2026-10-25). The owner, the night he opened his own ad: "I clicked on ad, it
   * opened the product page but didn't show a popup of trying to open in external browser? fix all!" The automatic
   * jump above was the 2026-10-08 ask; production says the app swallows an untapped scheme for 248 of 280 visitors,
   * so most shoppers saw nothing happen and nothing offered (the 2026-10-10 "remove, it looks very bad" made the
   * swallowed case silent on purpose). Now a bottom sheet (Components/InAppBrowserPrompt) asks, and the page leaves
   * ONLY on its tap — a tapped scheme is honoured, and it is not an unexpected redirect.
   *
   * THE GUARDS, re-stated for a sheet instead of a jump:
   *   1. the old "never when the address carries `handoff`" is gone INSIDE an app. Since 2026-10-25 the in-app page
   *      keeps `items=…&handoff=1` in its own address (utils/inAppAddress) so the app's "Open in browser" brings the
   *      basket, which would otherwise make this guard silence the prompt for everyone who has ever added something.
   *      The loop it prevented — a jump landing on a page that jumps again — cannot happen: nothing jumps without a
   *      tap, and a real browser never reaches this effect. (Outside an app the effect returns at its first line.)
   *   2. once per shop per session, and once per DAY per device, claimed when the sheet is SHOWN so a remount or the
   *      next link from the feed does not ask again. Staying is an answer;
   *   3. it waits for the shop (`shopSettledFor`) so `shop_open_in_browser_prompt` always has a salonId. */
  const [promptOpen, setPromptOpen] = useState(false);
  const [promptSwallowed, setPromptSwallowed] = useState(false);
  const promptTimerRef = useRef(null);
  const trackPrompt = (action) => {
    if (!salon?.id) return;
    trackStoreEvent({
      salonId: salon.id,
      eventType: 'shop_open_in_browser_prompt',
      payload: { action, os: phoneOsHere, productId: product?.id ?? null },
    });
  };
  useEffect(() => {
    // Off since 2026-11-02 (utils/productPagePrompt): the payment step offers the way out instead.
    if (!PRODUCT_PAGE_ASKS_TO_OPEN_THE_BROWSER) return;
    if (!inAppHere || typeof window === 'undefined') return;
    if (!salon?.id || shopSettledFor !== salonSlug) return;
    try {
      if (window.sessionStorage.getItem(AUTO_LEAVE_KEY)) return;
      window.sessionStorage.setItem(AUTO_LEAVE_KEY, '1');
    } catch (e) { return; /* no memory to stop a second ask with: do not start one */ }
    // And once a DAY per device, not once per webview (utils/openInBrowser.claimAutoLeave): the app hands each
    // link from the feed a fresh webview, so sessionStorage alone asked the same person on every product.
    let storage = null;
    try { storage = window.localStorage; } catch (e) { storage = null; }
    if (!claimAutoLeave(storage, AUTO_LEAVE_KEY)) return;
    setPromptOpen(true);
    trackPrompt('shown');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inAppHere, salon?.id, salonSlug, shopSettledFor]);
  useEffect(() => () => { if (promptTimerRef.current) clearTimeout(promptTimerRef.current); }, []);
  /* AFTER A TAP THE APP HONOURED, THE QUESTION IS ANSWERED (2026-10-25, final review). The page going hidden is the app
     handing it to Safari; a shopper coming back from Safari should find the product, not the sheet they already
     answered. Armed by the tap, gone at the first hiding or when the sheet closes another way. */
  const promptHiddenListenerRef = useRef(null);
  const disarmPromptHidden = () => {
    if (promptHiddenListenerRef.current && typeof document !== 'undefined') document.removeEventListener('visibilitychange', promptHiddenListenerRef.current);
    promptHiddenListenerRef.current = null;
  };
  useEffect(() => disarmPromptHidden, []);
  const promptTap = () => {
    trackPrompt('tap');
    disarmPromptHidden();
    if (typeof document !== 'undefined') {
      promptHiddenListenerRef.current = () => {
        if (document.visibilityState !== 'hidden') return;
        disarmPromptHidden();
        if (promptTimerRef.current) clearTimeout(promptTimerRef.current);
        setPromptOpen(false);
        setPromptSwallowed(false);
      };
      document.addEventListener('visibilitychange', promptHiddenListenerRef.current);
    }
    openThisInBrowser({ automatic: false });
    // The same proxy as the result event: 1.5 s on, a page still in front of the shopper was swallowed, so the
    // sheet now says where the app's own door is. A page that went hidden has nothing to explain.
    if (promptTimerRef.current) clearTimeout(promptTimerRef.current);
    promptTimerRef.current = setTimeout(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      setPromptSwallowed(true);
    }, HANDOVER_RESULT_MS);
  };
  const promptStay = () => {
    trackPrompt('stay');
    disarmPromptHidden();
    setPromptOpen(false);
    setPromptSwallowed(false);
  };


  /* WHAT THE PAGE SHOWED THEM — once per view, after the shop has settled.
   *
   * Everything else this page records is what the shopper DID: opened, tapped, added, left.
   * None of it says what they were looking at, so a question like "do the pages with a sale
   * card convert better than the ones without" cannot be asked at all — and that is most of
   * what there is to learn from a page that loses 96% of its visitors. This is the shape of
   * the page as rendered: the facts a reader can group by.
   *
   * Only page shape. No prices typed by anyone, nothing about the person, and the ids are the
   * ones every other event here already carries. It waits for `shopSettledFor` for the same
   * reason the view does: before the shop's campaigns land, "discounted" is not yet true. */
  const shownRef = useRef(null);
  useEffect(() => {
    if (!salon?.id || !product?.id || shopSettledFor !== salonSlug) return;
    const key = `${product.id}:${salonSlug}`;
    if (shownRef.current === key) return;
    shownRef.current = key;
    const photos = Array.isArray(images) ? images.length : 0;
    trackStoreEvent({
      salonId: salon.id,
      eventType: 'product_shown',
      payload: {
        productId: product.id,
        variantId: selectedVariant?.id ?? null,
        // the offer
        discounted: !!discounted,
        discountPct: discounted ? discountPct : null,
        saleCard: !!campaignPricing,
        // what there is to choose, and whether it can be bought at all
        colours: colorOptions.length,
        variants: (product.variants || []).length,
        outOfStock: !!isOutOfStock,
        // what else the page had to offer as a reason
        photos,
        promise: !!shownReturns || freeShippingThreshold > 0,
        importer: product.brand?.isOfficialImporter === true,
        reviews: Number(reviewSummary?.count) || 0,
        shopReviews: Number(salon?.shopReviews?.count) || 0,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salon?.id, product?.id, shopSettledFor, salonSlug]);

  /* What the basket counts towards free delivery — for the bar and for "₪X more"
   * in the delivery tab: measured on the amount the business chose
   * (`freeDeliveryPromo.basis`, after discounts unless it said before), as the
   * checkout measures it (CHK-23 / DISC-32g). */
  const cartTotal = useFreeDeliverySpend(salonSlug, cart, {
    basis: freeDeliveryPromo?.basis,
    threshold: freeShippingThreshold,
  });

  // Bump the floating cart badge whenever the count grows (item added).
  useEffect(() => {
    if (cartCount === prevCartCountRef.current) return undefined;
    const grew = cartCount > prevCartCountRef.current;
    prevCartCountRef.current = cartCount;
    if (!grew) return undefined;
    setCartBump(true);
    const id = setTimeout(() => setCartBump(false), 380);
    return () => clearTimeout(id);
  }, [cartCount]);

  // The tab: "{product} | {shop}" in the reader's language — the title the page's head arrives with
  // — for every shop (productTabTitle). Seen live on 2026-09-17, a loaded product page read "Stylon".
  useDocumentTitle(productTabTitle(salon, shopEntityName(product, i18n.language)));
  /* THE FLOATING HELP BUTTON NAMES THIS PRODUCT (DK parity, S1d): the page remembers what it shows
   * while it shows it, for the button's first WhatsApp line, and forgets it on the way out. */
  useEffect(() => {
    rememberCurrentProductName(product?.name || null);
    return () => rememberCurrentProductName(null);
  }, [product?.name]);
  /* On a phone the add-to-cart bar holds the bottom of the screen (the cart bar steps aside here),
   * so it publishes its height as the cart bar does, and the help button rises above it. Hidden from
   * 900px by the stylesheet, it measures 0 there and publishes nothing. */
  const stickyCtaRef = useRef(null);
  const [actionsOnHero, setActionsOnHero] = useState(
    typeof window !== 'undefined' && window.matchMedia?.('(max-width: 899px)').matches === true,
  );
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(max-width: 899px)');
    const onChange = (e) => setActionsOnHero(e.matches);
    setActionsOnHero(mq.matches);
    if (mq.addEventListener) { mq.addEventListener('change', onChange); return () => mq.removeEventListener('change', onChange); }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);

  /* WHEN THE PHOTOGRAPH IS COVERED, THE HEADER SAYS WHAT YOU ARE LOOKING AT (2026-10-07).
   * The owner, with the reference open: "when fully covering the pic, we can switch it to
   * status of showing the name of the product or something in the top bar, with the back
   * button and the love button." Scrolled past the picture there is nothing on screen naming
   * the product — the name has scrolled away with it — so the bar that is always there takes
   * the job. An observer on the stage itself, not a scroll listener: the answer is "is the
   * photograph still on screen", which is the question an IntersectionObserver answers without
   * running anything on every frame. */
  /* "ONCE EVER MAKE IT SHOW A POPUP YOU CAN ORDER FROM HERE" (2026-10-07, the owner). A green
   * circle is not self-explaining to DK's shoppers, who are 55+ and arrived from an ad: it
   * says WhatsApp, not "this is a way to buy". So the first time a shopper meets it, and only
   * the first time, a small bubble says what it does, and then it never appears again.
   * localStorage can throw outright in a private window and can come back empty with site data
   * cleared, so both sides are wrapped and the failure mode is the quiet one: no bubble. */
  const [waBubble, setWaBubble] = useState(false);
  /* The bubble's own effect sits above the page's early returns and `noteContactBubble` is
   * built below them, so the effect reaches it through a ref rather than closing over it. */
  const noteContactBubbleRef = useRef(null);
  /* NOT AT FIRST PAINT (Swe Sosetta run 3, panel S10): 1.2 s after the page arrived the bubble popped over «اشترِ الآن»
   * while the shopper was still reading the price. It waits for the shopper's first scroll — the page is theirs first. */
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    let seen = true;
    try { seen = !!window.localStorage.getItem('stylon:wa-order-hint'); } catch (e) { seen = true; }
    if (seen) return undefined;
    let show = null;
    let hide = null;
    const start = () => {
      window.removeEventListener('scroll', start);
      show = setTimeout(() => {
        setWaBubble(true);
        noteContactBubbleRef.current?.();
        try { window.localStorage.setItem('stylon:wa-order-hint', '1'); } catch (e) { /* nothing to remember it with */ }
      }, 1200);
      hide = setTimeout(() => setWaBubble(false), 8200);
    };
    window.addEventListener('scroll', start, { passive: true });
    return () => {
      window.removeEventListener('scroll', start);
      if (show) clearTimeout(show);
      if (hide) clearTimeout(hide);
    };
  }, []);

  /* THE FLOATING CIRCLE STEPS ASIDE WHILE AN INLINE ASK IS ON SCREEN (Swe Sosetta run 3, panel S7): the tray's page
   * showed WhatsApp twice — its wholesale ask under the price and the circle over the page. The element is held in
   * state (a callback ref), because the ask appears only once the shop's channels arrive. */
  const [inlineAskEl, setInlineAskEl] = useState(null);
  const [inlineAskOnScreen, setInlineAskOnScreen] = useState(false);
  useEffect(() => {
    if (!inlineAskEl || typeof IntersectionObserver !== 'function') { setInlineAskOnScreen(false); return undefined; }
    const obs = new IntersectionObserver((entries) => {
      const mine = entries.filter((e) => e.target === inlineAskEl);
      if (mine.length) setInlineAskOnScreen(mine[mine.length - 1].isIntersecting);
    }, { threshold: 0 });
    obs.observe(inlineAskEl);
    return () => obs.disconnect();
  }, [inlineAskEl]);

  /* THE DOCKED ADD ASKS ONLY WHILE QUICK BUY IS OFF THE SCREEN (the trial gate, 10-29): quick buy names a missing choice
   * in the page, so while the shopper can see it the add under it says only its word — one control asks. "On the
   * screen" is above the docked bar, which covers the bottom of it, and whole: a button half under the bar is not one
   * the shopper reads. A callback ref, like the inline ask's: quick buy is drawn only once the product has arrived. */
  const [quickBuyEl, setQuickBuyEl] = useState(null);
  const [quickBuyOnScreen, setQuickBuyOnScreen] = useState(false);
  useEffect(() => {
    if (!quickBuyEl || typeof IntersectionObserver !== 'function') { setQuickBuyOnScreen(false); return undefined; }
    const barHeight = stickyCtaRef.current?.offsetHeight || 0;
    const obs = new IntersectionObserver((entries) => {
      const mine = entries.filter((e) => e.target === quickBuyEl);
      if (mine.length) setQuickBuyOnScreen(mine[mine.length - 1].isIntersecting);
    }, { threshold: 1, rootMargin: `0px 0px -${barHeight}px 0px` });
    obs.observe(quickBuyEl);
    return () => obs.disconnect();
  }, [quickBuyEl]);

  /* THE FLOATERS STEP ASIDE FOR THE READING (Swe Sosetta run 4, round B: S8). The WhatsApp circle and the chat's
   * launcher float in the band just above the buy bar — where, on a phone's first screen, the tabs and the first lines
   * of the description stand: Luna covered the first words of every description. While the tabs and the description's
   * first lines (`readingHeadEl`, an invisible box over them) are in the bottom quarter of the screen, the circle is not
   * drawn and the page tells the launcher to step aside (`html[data-floaters-aside]`, agentChat.module.css). */
  const [readingHeadEl, setReadingHeadEl] = useState(null);
  const [floatersAside, setFloatersAside] = useState(false);
  useEffect(() => {
    if (!readingHeadEl || typeof IntersectionObserver !== 'function') { setFloatersAside(false); return undefined; }
    const obs = new IntersectionObserver((entries) => {
      const mine = entries.filter((e) => e.target === readingHeadEl);
      if (mine.length) setFloatersAside(mine[mine.length - 1].isIntersecting);
    }, { threshold: 0, rootMargin: '-75% 0px 0px 0px' });
    obs.observe(readingHeadEl);
    return () => obs.disconnect();
  }, [readingHeadEl]);
  useEffect(() => {
    const root = typeof document !== 'undefined' ? document.documentElement : null;
    if (!root) return undefined;
    if (floatersAside) root.setAttribute('data-floaters-aside', '');
    else root.removeAttribute('data-floaters-aside');
    return () => root.removeAttribute('data-floaters-aside');
  }, [floatersAside]);

  const [heroGone, setHeroGone] = useState(false);
  const heroEndRef = useRef(null);
  useEffect(() => {
    const node = heroEndRef.current;
    if (!node || typeof IntersectionObserver !== 'function') return undefined;
    /* NOT the photograph itself. The stage is `position: sticky` on a phone, so it is pinned to
     * the top of the screen for the whole page and an observer on it answers "still visible"
     * forever — measured at a scroll of 900, the stage still reported top 0, bottom 226, with
     * the detail card painted over it. The question the header actually asks is "has the card
     * covered the picture", so the thing to watch is the seam between them: a marker sitting
     * where the card begins, with the header's own height taken off the top of the root. */
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => setHeroGone(!e.isIntersecting)),
      { threshold: 0, rootMargin: '-64px 0px 0px 0px' },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [product?.id]);
  usePublishedHeight(stickyCtaRef, { name: '--sticky-cart-offset', key: 'product-cta', active: !!product });
  /* The header's real height, measured, because the photograph is pulled up by exactly it.
   * A constant was tried first and was wrong: 46 by arithmetic (6 + 34 + 6) against 62 on the
   * page, so the picture started 16px down the screen with a white band above it. The
   * difference is line boxes — the title's and the buttons' — which arithmetic on the parts
   * does not see. The bar publishes what it actually measures, the way the buy bar does. */
  const topBarRef = useRef(null);
  usePublishedHeight(topBarRef, { name: '--pd-topbar-h', key: 'product-topbar', active: !!product });

  // The branded domain resolves to no business: that, and not a missing
  // product, is what went wrong — so say the true thing.
  if (salonSlugUnresolved) {
    return (
      <div className={styles.page}>
        <BusinessNotFound />
      </div>
    );
  }
  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.centerMsg}>{t('common.loading', { defaultValue: 'Loading…' })}</div>
      </div>
    );
  }
  if (notFound || !product) {
    return (
      <div className={styles.page}>
        <div className={styles.centerMsg}>
          <h2>{t('productDetail.notFoundTitle', { defaultValue: 'Product not found' })}</h2>
          <p>{t('productDetail.notFoundBody', { defaultValue: 'This product may have been removed or the link is wrong.' })}</p>
        </div>
        {/* Still a page of the shop, and its pixel still counts it. */}
        <PrivacyNotice t={t} />
      </div>
    );
  }

  /* TAKE THE SHOPPER TO THE CHOICE THAT IS MISSING.
   *
   * The first unchosen axis comes into the middle of the screen, outlined, with
   * one line saying what to choose (`role="alert"`, so a screen reader hears
   * it), and its first option takes focus without a second scroll.
   *
   * AND THE COLOUR SHEET OPENS OVER IT. Seven refused adds from five shoppers in
   * five days on DK's ROMA luggage set (product_cart_blocked, choice_required, to
   * 26 Sept 2026), and every one of them left: the tap scrolled, said what to
   * choose, and then wanted a tap on a colour and another on the bar. The sheet
   * lays every colour beside its own photos and ONE add button, which says
   * `chosen` (round 13) — so the tap after a refusal adds. It opens only where
   * the page has a sheet to open, a colour to choose among; a size or a type
   * alone keeps the scroll, which lands on the very chips they need. The line
   * stays under it for a sheet closed with nothing chosen. */
  const askForChoice = () => {
    setChoiceAsked(true);
    if (colorOptions.length > 1) setColorModalOpen(true);
    const root = pickerRef.current;
    const section = root?.querySelector(`[data-axis="${pendingAxes[0]}"]`) ?? root;
    if (section && typeof section.scrollIntoView === 'function') {
      section.scrollIntoView({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }
    const firstOption = section?.querySelector('button[aria-pressed]');
    if (firstOption && typeof firstOption.focus === 'function') {
      try { firstOption.focus({ preventScroll: true }); } catch (_) { /* old WebViews */ }
    }
  };

  /* ORDER BY WHATSAPP OR BY PHONE (2026-10-01). DK's shoppers are 55+ and arrive from an ad inside Instagram's
   * browser; eleven of them this week pressed add-to-cart before choosing a colour, and of those who reached the
   * checkout most typed nothing. A message to the shop naming the watch, the colour, the quantity and the price is
   * the way many of them already buy. The shop's own number, under the owner's own switches (utils/contactOrder.js);
   * the tap is recorded as `shop_contact_order`, a stage of its own in the shop's funnel — never a purchase. */
  // Not a hook: this sits below the page's own early returns, and the message is a few strings.
  /* IT NAMES ONLY WHAT THE BUYER CHOSE (Swe Sosetta run 3, panel S3). The page shows its first variant so there is a
   * photo and a price; the message used to name that variant's flavour — «نكهة: دبي» before any flavour was tapped —
   * and link to it, and the shop would bake what nobody ordered. An option is named once the buyer chose it (a tap, or
   * the ad link they came by); a choice with one option is no choice and is named as the fact it is. The variant is
   * linked, and a name-only variant named, only when nothing is left to choose. */
  const contactOrder = (() => {
    if (!product || !salon) return { any: false, whatsappUrl: null, callHref: null };
    const allChosen = pendingAxes.length === 0;
    const text = productOrderMessage({
      t,
      shopName: salon.name,
      productName: product.name + (allChosen && selectedVariant?.name ? ` — ${selectedVariant.name}` : ''),
      options: variantOptionsIn(product, selectedVariant, i18n.language, t).filter((o) => !isPending(o.key)),
      quantity: qty,
      /* THE LINE'S TOTAL after the quantity (run 4, round B: S3): «× 3 — 13 ₪» read as three cookies for 13; the
       * checkout says «× 3 — 39 ₪», and so does this. */
      price: price != null && Number.isFinite(Number(price)) ? shopPrice(Number(price) * Math.max(1, Number(qty) || 1), currency) : shopPrice(price, currency),
      url: typeof window !== 'undefined' && window.location
        ? `${window.location.origin}${reviewProductHref}${allChosen && selectedVariant?.id ? `?variant=${encodeURIComponent(selectedVariant.id)}` : ''}`
        : '',
    });
    return contactOrderChannels(salon, text);
  })();
  /* AN "ASK US" PRODUCT IS ORDERED BY ASKING (2026-10-25). The owner sold it by asking — a school giveaway, a cake made
   * to order — so this page sells nothing by cart for it: no quantity, no add-to-cart, no buy-now. Its one action is the
   * order button's own path, not a second implementation: the same channels (utils/contactOrder → utils/whatsappLink, so
   * the same plain same-frame link inside Instagram's browser), the same `shop_contact_order` tap (the ad agent counts it
   * as "ordered by WhatsApp"), the same "did anything open?" sheet. Only the words differ: nothing is being ordered and
   * there may be no price, so the draft is the shop's "I came from your shop about <product>" and the page's address.
   * The same text serves the wholesale line on a cart product that has one. */
  const asks = isAskProduct(product);
  const noPrice = hidesItsPrice(product);
  /** A value's own price under its name, or null: none when its slot's values cost the same or the price is hidden. */
  const choicePriceOf = (slot, value) => (noPrice ? null : choicePrices[slot]?.get(value) ?? null);
  /** The id that ties a value's price to its button as the button's description (its name stays the value's). */
  const choicePriceId = (slot, value) => (choicePriceOf(slot, value) == null ? undefined : `${choicePriceIds}${slot}-${encodeURIComponent(value)}`);
  const askChannels = (() => {
    if (!product || !salon) return { any: false, whatsappUrl: null, callHref: null };
    const address = typeof window !== 'undefined' && window.location
      ? `${window.location.origin}${reviewProductHref}${pendingAxes.length === 0 && selectedVariant?.id ? `?variant=${encodeURIComponent(selectedVariant.id)}` : ''}`
      : '';
    /* EACH ASK DRAFTS ITS OWN MESSAGE (Swe Sosetta run 3, panel S6). Both used to open on the machine-like «وصلت من
     * متجر …» and prompt for nothing: an ask product is booked — the number and the date to fill in — and a cart
     * product's wholesale line asks for the big-order price, for how many and when. */
    const words = asks
      ? t('productDetail.askPrefill', { product: product.name, defaultValue: 'Hello, I would like to book “{{product}}”: quantity ___ and date ___' })
      : t('productDetail.wholesalePrefill', { product: product.name, defaultValue: 'Hello, I would like to ask about the wholesale price of “{{product}}” for a party / a big order: how many ___ and the date ___' });
    return contactOrderChannels(salon, [words, address].filter(Boolean).join('\n'));
  })();
  /* THE WHATSAPP ORDER, END TO END (2026-10-07, the owner: "and logs for opening and clicking
   * whatsapp button").
   *
   * The tap was already recorded. What was NOT recorded is the thing that actually went wrong
   * on 2026-10-03: eight shoppers tapped this and the shop received nothing, because the link
   * carried `target="_blank"` and inside Facebook's and Instagram's browsers a new tab lands on
   * WhatsApp Web's login wall instead of handing the address to the app. Since 2026-10-14 the
   * link is the plain wa.me address with no target and no window.open — a universal / app link
   * the tap itself navigates to (utils/whatsappLink.js). The page already DETECTS the failure — if it is still visible 1.5s after
   * the tap, the app did not take over, and it shows the "WhatsApp didn't open?" line — and it
   * then threw the finding away. It is an event now, so the eight are countable next time.
   *
   * `opened` is the honest word for it: the page going hidden means something else took the
   * screen. It is a proxy, not a delivery receipt — nothing here can see a message arrive. */
  const noteContactOrder = (channel, where, asking = false) => {
    if (!salon?.id || !product?.id) return;
    // `ask: true` only on the ask taps, so they can be told from orders; the event and its counting are the same.
    const base = { channel, where, ...(asking ? { ask: true } : {}), productId: product.id, variantId: selectedVariant?.id ?? null, inApp: !!contactOrder.inApp };
    trackStoreEvent({ salonId: salon.id, eventType: 'shop_contact_order', payload: base });
    if (channel !== 'whatsapp') return;
    /* A TAP THAT TAKES THE PAGE WITH IT (2026-10-14, the owner: "make sure we have full coverage of
     * movements in the website").
     *
     * The timer below can only speak for a tap the page SURVIVES. This link is tapped in the same
     * frame — `x-safari-https://wa.me/…` on an iPhone inside an app's browser, WhatsApp's
     * `intent://` on Android, a plain wa.me link everywhere else — so when the web view navigates
     * the frame rather than handing the address to an app, this document is unloaded and 1.5 s
     * never arrives. Those taps have left a `shop_contact_order` and no result at all: visible as a
     * tap, invisible as an outcome, and they are the very taps that went wrong.
     *
     * The store's event queue already collects a last word as the page goes and sends it in the
     * closing keepalive request (api/storeApi.js `beforeStoreEventsLeave` → flushStoreEventsOnExit),
     * which is the only door a sending has at that moment. `opened: null` because a page being
     * unloaded knows nothing about whether WhatsApp got the message, and `left: true` because THAT
     * is the fact worth keeping.
     *
     * ONLY `pagehide`, NOT A PAGE MERELY GOING HIDDEN. Hidden is what a successful hand-off to the
     * app looks like — it is this event's whole definition of `opened` — and it is also the first
     * thing a browser does when it unloads a document. Nothing here can tell those apart in the
     * moment, so treating hidden as a leaving would quietly rewrite every successful hand-off from
     * `opened: true` to `opened: null` and destroy the number this event exists for. `pagehide`
     * says the document is actually going; a page that only went behind another app comes back to
     * its own timer. */
    let said = false;
    const stopWatchingForTheLeave = beforeStoreEventsLeave((reason) => {
      if (said || reason !== 'pagehide') return;
      said = true;
      stopWatchingForTheLeave();
      trackStoreEvent({ salonId: salon.id, eventType: 'shop_contact_order_result', payload: { ...base, opened: null, left: true } });
    });
    setTimeout(() => {
      stopWatchingForTheLeave();
      if (said || typeof document === 'undefined') return;
      said = true;
      const opened = document.visibilityState === 'hidden';
      trackStoreEvent({ salonId: salon.id, eventType: 'shop_contact_order_result', payload: { ...base, opened, left: false } });
      if (!opened && contactOrder.inApp) setWaHintShown(true);
    }, HANDOVER_RESULT_MS);
  };

  /* The one-time bubble, counted: it is the page teaching the shopper that this circle is a way
   * to buy, and whether it earns its place is a question about taps AFTER it was seen. */
  const noteContactBubble = () => {
    if (!salon?.id || !product?.id) return;
    trackStoreEvent({ salonId: salon.id, eventType: 'shop_contact_order_hint', payload: { productId: product.id, inApp: !!contactOrder.inApp } });
  };
  noteContactBubbleRef.current = noteContactBubble;
  /* ONE COPY, NOT TWO WITH ONE HIDDEN (2026-10-07).
   *
   * The first cut of the hero overlay rendered this block twice and let a media query hide the
   * one that did not apply. In a browser that is invisible; in the DOM it is two buttons with
   * the same `data-testid` and the same `aria-label`, and two suites went red saying exactly
   * that ("Found multiple elements by: product-detail-like"). A screen reader in a browser
   * without CSS, or any tool reading the tree rather than the paint, would have found the same
   * pair. So the viewport is asked once and only the copy that applies is rendered.
   *
   * The fallback, when there is no `matchMedia` at all (jsdom, and a browser old enough that
   * this page has bigger problems), is the in-flow copy: the placement this page shipped with. */
  /* WHICH OF THE TWO, AND WHERE (2026-10-07). The owner: "maybe keep the love button in the
   * top, but the share maybe move to another place? in the previous place we had it? to keep
   * the top a less traffic jammed" — and the reference's header carries Back and a heart and
   * nothing else. On a phone the header gets the heart alone and Share goes back to the row
   * under the buy button, where it was; a desktop has no floating header and keeps both there.
   * One renderer either way, so the handlers and the liked state cannot drift apart. */
  const renderTitleActions = (where, show = ['like', 'share']) => (
    <div
      className={`${styles.titleActions} ${where === 'top' ? styles.titleActionsInBar : ''}`}
      data-where={where}
    >
      {show.includes('like') && (<button
        type="button"
        className={`${styles.iconAction} ${liked ? styles.iconActionLiked : ''}`}
        aria-label={liked
          ? t('productDetail.unlike',  { defaultValue: 'Remove from favorites' })
          : t('productDetail.like',    { defaultValue: 'Add to favorites' })}
        aria-pressed={liked}
        onClick={handleToggleLike}
        disabled={likeBusy}
        data-testid="product-detail-like"
      >
        {liked ? (
          /* Filled red heart when favorited (mask of the shared icon). */
          <span
            className={styles.iconHeartFilled}
            style={{ maskImage: `url(${heartIcon})`, WebkitMaskImage: `url(${heartIcon})` }}
            aria-hidden
          />
        ) : (
          /* The shared outline heart as-is. */
          <img src={heartIcon} className={styles.iconActionImg} alt="" aria-hidden />
        )}
        <span className={styles.iconActionLabel}>
          {liked
            ? t('productDetail.saved', { defaultValue: 'Saved' })
            : t('productDetail.save', { defaultValue: 'Save' })}
        </span>
          </button>)}
      {show.includes('share') && (<button
        type="button"
        className={styles.iconAction}
        aria-label={t('productDetail.share', { defaultValue: 'Share' })}
        onClick={handleShare}
        data-testid="product-detail-share"
      >
        <img src={exportIcon} className={styles.iconActionImg} alt="" aria-hidden />
        <span className={styles.iconActionLabel}>
          {t('productDetail.share', { defaultValue: 'Share' })}
        </span>
          </button>)}
    </div>
  );

  /* On a phone the words are hidden and this link is a circle (2026-10-07), so the accessible
     name has to be carried by aria-label or the link reaches a screen reader unnamed. */
  const renderContactOrder = (testId, where = 'inline', { ask = false, quiet = false, rowRef = undefined } = {}) => {
    /* The ask button is the same link on the same path with its own look: it is a primary action, and the quiet
     * `contactOrderRow` is hidden inside the buy row on every width (the WhatsApp circle floats instead). */
    const channels = ask ? askChannels : contactOrder;
    if (!channels.any) return null;
    /* NO ORDER BEFORE THE CHOICE (run 4, round B: S3). The cart refuses an add with a flavour left to choose, and the
     * WhatsApp order went out without one — «كوكيز (الحبة) × 3» and the shop to ask which. While a choice is waiting the
     * order button is not a link: it says what to choose in the add button's own words and its tap goes to the choice,
     * as the add button's does. */
    /* Whatever the stock of the variant the page happens to show (fix round 1): a sold-out first flavour must not let
     * an order with no flavour out. */
    const waitingFor = !ask && channels.whatsappUrl && pendingAxes.length > 0 && pendingAxisName
      ? t('productDetail.chooseFirst', { defaultValue: 'Choose {{axis}}', axis: pendingAxisName })
      : null;
    if (waitingFor) {
      const rowClassW = quiet ? styles.bulkAskRow : styles.contactOrderRow;
      /* NOT DISABLED — its tap asks for the choice (fix round 3, #4b, as the add buttons): no aria-disabled. It shows
       * WhatsApp's own word with the choice as a hint, and its name is that word, then the choice (WCAG 2.5.3):
       * «الطلب عبر واتساب، اختاروا نكهة». */
      const door = t('productDetail.orderByWhatsapp', { defaultValue: 'Order by WhatsApp' });
      const name = t('productDetail.addWaitingName', { defaultValue: '{{door}}, {{choice}}', door, choice: waitingFor });
      return (
        <div ref={rowRef} className={rowClassW} data-testid={testId}>
          <button
            type="button"
            className={`${styles.contactOrderLink} ${styles.contactOrderWhatsapp} ${styles.contactOrderWaiting}`}
            aria-label={name}
            title={name}
            onClick={askForChoice}
            data-waiting="choice"
            data-testid={`${testId}-whatsapp`}
          >
            <FaWhatsapp aria-hidden />
            <span className={styles.addWaitingWords}>
              <span className={styles.addWaitingDoor} data-label>{door}</span>
              <span className={styles.addWaitingHint} data-hint>{waitingFor}</span>
            </span>
          </button>
        </div>
      );
    }
    const label = ask
      ? t('productDetail.askOnWhatsapp', { defaultValue: 'Ask on WhatsApp' })
      : t('productDetail.orderByWhatsapp', { defaultValue: 'Order by WhatsApp' });
    /* `quiet`: an ask BESIDE buying — a cart product's wholesale line — is the outlined green link, never the filled
     * primary an ask product leads with (panel S7). */
    const rowClass = quiet ? styles.bulkAskRow : (ask ? styles.askRow : styles.contactOrderRow);
    const linkClass = quiet ? styles.bulkAskLink : (ask ? styles.askLink : `${styles.contactOrderLink} ${styles.contactOrderWhatsapp}`);
    return (
    <div ref={rowRef} className={rowClass} data-testid={testId}>
      {channels.whatsappUrl ? (
        <>
          <a
            className={linkClass}
            href={channels.whatsappUrl}
            target={channels.inApp ? undefined : '_blank'}
            rel="noopener noreferrer"
            onClick={() => noteContactOrder('whatsapp', where, ask)}
            data-testid={`${testId}-whatsapp`}
            aria-label={label}
          >
            <FaWhatsapp aria-hidden />
            {label}
          </a>
          {waHintShown && (
            /* Not a line of text any more: the doors that work from inside an app's browser (Components/ContactOrderFallback). */
            <ContactOrderFallback
              t={t}
              salonSlug={salonSlug}
              shopName={salon?.name}
              number={channels.whatsappNumber}
              callHref={channels.callHref}
              countryCode={salon?.country}
              productId={product?.id ?? null}
              variantId={selectedVariant?.id ?? null}
              productName={product?.name ?? null}
              page="product"
              onNoted={(channel) => noteContactOrder(channel, where, ask)}
              testId={`${testId}-whatsapp-hint`}
            />
          )}
        </>
      ) : (
        <a className={quiet ? styles.bulkAskLink : (ask ? styles.askLink : styles.contactOrderLink)} href={channels.callHref} onClick={() => noteContactOrder('phone', where, ask)} data-testid={`${testId}-phone`}>
          <FiPhone aria-hidden />
          {ask ? t('productDetail.askByPhone', { defaultValue: 'Call to ask' }) : t('productDetail.orderByPhone', { defaultValue: 'Call to order' })}
        </a>
      )}
    </div>
    );
  };

  /* An ask-us product's one action — or, when the shop has neither WhatsApp nor a phone to ask on, one plain line saying
   * so. No link: there is nothing to link to, and a bare button that does nothing is worse than words. */
  const renderAskOrNoChannel = (testId, where) => (askChannels.any
    ? renderContactOrder(testId, where, { ask: true })
    : (
      <p className={styles.askNoChannel} data-testid={`${testId}-nochannel`}>
        {t('productDetail.askNoChannel', { defaultValue: 'Sold by asking the shop — contact them to order' })}
      </p>
    ));

  const handleAddToCart = (mode = 'add', { chosen = false } = {}) => {
    /* An add that puts nothing in is a step too (utils/shopJourney.js); one that does is told by the
     * cart, which knows how many units went in (Context/CartContext). */
    const refused = (errorCode) => {
      journeyCartAdd({ productId: product.id, variantId: selectedVariant?.id, ok: false, errorCode });
      /* An add that put nothing in, for EVERY shopper — the journey above keeps only those who came from an ad, so a
       * shop losing people at the choice could not see it (21 Sept 2026: not one refusal was on record). */
      if (salon?.id && product?.id) {
        trackStoreEvent({ salonId: salon.id, eventType: 'product_cart_blocked', payload: { productId: product.id, variantId: selectedVariant?.id ?? null, reason: errorCode } });
      }
    };
    if (isOutOfStock || !matchedVariant) { refused('out_of_stock'); return; }
    /* NO ADD BEFORE THE CHOICE. The bar, the desktop button, "Buy now" and the
     * full-screen viewer all come through here; the colour sheet, which shows
     * every option beside its own add button, says `chosen`. */
    if (pendingAxes.length > 0) {
      if (!chosen) {
        setChoiceMode(mode);
        refused('choice_required');
        askForChoice();
        return;
      }
      setChosenAxes(new Set(['color', 'size', 'style']));
    }
    setChoiceAsked(false);
    /* NOTHING TO ADD IS NOT "ADDED".
     *
     * The cart holds a line at the shelf's ceiling and refuses anything past
     * it, so with the last unit already in the basket a press here changes
     * nothing — and the button used to say "נוסף לסל ✓" anyway (UX-SHOP-15).
     * The drawer already says the true thing ("זו כל הכמות שיש במלאי"); this
     * says it where the press happened. "Buy now" is never refused here: the
     * line it came for is in the basket, and it goes to pay (below). */
    const shelfCap = variantPurchaseCap(product, selectedVariant);
    const inCart = (cart?.items ?? []).find((i) => i.variantId === selectedVariant?.id)?.quantity ?? 0;
    /* ONLY A `+` CAN HAVE NOTHING TO ADD. "Buy now" makes sure the line is in — at the stepper's number when it
     * is new, never fewer than the shopper built when it is not — and goes to pay; a line already at the
     * ceiling is what they are buying, not a refusal. */
    if (mode === 'add' && hasCap(shelfCap) && inCart >= shelfCap) {
      refused('stock_cap');
      setJustAdded(false);
      setCapNote(shelfCap);
      if (addedTimerRef.current) clearTimeout(addedTimerRef.current);
      addedTimerRef.current = setTimeout(() => setCapNote(null), 3200);
      return;
    }
    setCapNote(null);
    const options = variantOptionsIn(product, selectedVariant, i18n.language, t);
    const snapshot = {
      name: product.name + (selectedVariant?.name ? ` — ${selectedVariant.name}` : ''),
      image: images?.[0]?.url ?? null,
      // THE LIST PRICE, never the campaign price on the screen. The cart used
      // to hold what this page showed (₪492 in a 15% sale) while every other
      // door stored the shelf price, and the checkout printed that line above
      // a list-price subtotal AND a discount row — the sale taken twice on
      // paper. The server prices the order from the variant id regardless.
      price: campaignPricing.listPrice,
      // The stepper above already refuses to go past this; the cart has to know
      // it too, because the drawer's `+` is a second door onto the same line.
      maxQty: variantPurchaseCap(product, selectedVariant),
      // The maker this page leads with, carried so the basket leads with it too (UX-SHOP-24).
      brand: brandName(product?.brand, i18n?.language) || null,
      options,
      variantAttrs: {
        color: selectedVariant?.color ?? null,
        size: selectedVariant?.size ?? null,
        style: selectedVariant?.style ?? null,
      },
    };
    /* TWO BUTTONS, TWO MEANINGS (2026-10-02, corrected 2026-10-06). "Add to cart" is one more of this — `qty`
     * units ON TOP of the line, and the drawer opens. "Buy now" is I AM BUYING THIS, TAKE ME TO PAY: the same
     * `qty` goes in on top of the line (the owner, on his phone: "I added once to cart and then I clicked quick
     * buy, but it shows only one item instead of 2") and the page goes to the checkout — EXCEPT on a retry. Both
     * were the same add for three weeks, and a DK shopper whose payment failed pressed "Buy now" again, four
     * times, and was asked for ₪2,460 instead of ₪492: nothing empties the cart on a failed payment, so each
     * press added a set. The cart tells a first press from a retry by the note the first one leaves
     * (Context/CartContext `buyNow`, `stylon:buy-intent:<shop>`), and a retry ENSURES the line instead of growing
     * it: the second press asks for the same money as the first. The pixel hears only what the line gained. */
    window.dispatchEvent(
      new CustomEvent('stylon:add-to-cart', {
        // `shownPrice`: one unit at the price row's number, campaigns counted — what the pixel is
        // told the add is worth (Context/CartContext). The line itself stores the list price above.
        detail: { salonSlug, productId: product.id, variantId: selectedVariant.id, quantity: qty, snapshot, shownPrice: price, mode },
      }),
    );

    if (salon?.id && product?.id) {
      trackStoreEvent({
        salonId: salon.id,
        eventType: 'product_cart_add',
        payload: {
          productId: product.id,
          variantId: selectedVariant?.id ?? null,
          quantity: qty,
          // Which door: 'buy' goes straight to the checkout, 'add' opens the drawer (2026-10-02).
          mode,
        },
      });
    }

    if (mode === 'buy') {
      navigate(`${getBasePath(salonSlug)}/checkout`);
      return;
    }

    /* SAY WHAT WENT IN, AND WHERE TO PAY. The cart drawer (Components/CartBar)
     * opens on this event whether or not its bottom bar is on screen — on a
     * phone it is not, here — naming the line just added, with the subtotal,
     * the checkout and "continue shopping". Dispatched after the add, in the
     * same tap, so the drawer's first frame already holds the new line. */
    /* THE FLIGHT FIRST, THEN THE DRAWER (2026-10-12, the owner: "added to cart status should be for a moment and fly to
     * the cart icon and then back to add to cart"). The drawer used to open in the same tick and cover the bag the
     * picture was flying to, so the flight was never seen. Now the picture lands on the bag (spawnFlyToCart, ~0.7 s)
     * and only then the drawer opens; the button says "added" for the moment and goes back to "add". */
    const flew = spawnFlyToCart();
    const openDrawer = () => window.dispatchEvent(new CustomEvent('stylon:open-cart', {
      detail: { addedVariantId: selectedVariant.id },
    }));
    if (flew) {
      if (drawerTimerRef.current) clearTimeout(drawerTimerRef.current);
      drawerTimerRef.current = setTimeout(openDrawer, FLY_TO_CART_MS);
    } else {
      openDrawer();
    }

    setJustAdded(true);
    if (addedTimerRef.current) clearTimeout(addedTimerRef.current);
    addedTimerRef.current = setTimeout(() => setJustAdded(false), 1800);
  };

  const toggleWishlist = async () => {
    if (wishBusy) return;
    setWishBusy(true);
    const wasWish = wishlisted;
    setWishlisted(!wasWish);
    const variantIdForWish = matchedVariant?.id ?? null;
    try {
      if (wasWish) await removeFromWishlist(product.id, variantIdForWish);
      else         await addToWishlist(product.id, variantIdForWish);
    } catch (err) {
      setWishlisted(wasWish);
      if (err?.response?.status === 401) navigate('/login');
    } finally {
      setWishBusy(false);
    }
  };

  // The wishlist CTA takes the prominent button slot (inline on desktop, the
  // sticky bottom bar on mobile) whenever the picked option is out of stock —
  // replacing the dead "Out of stock" button so it's impossible to miss.
  const renderWishlistCta = (extraClass, testId) => (
    <button
      type="button"
      className={`${styles.addToCartBtn} ${styles.wishlistCtaBtn} ${extraClass} ${wishlisted ? styles.wishlistCtaBtnActive : ''}`}
      onClick={toggleWishlist}
      disabled={wishBusy}
      data-testid={testId}
    >
      {wishlisted ? <FiCheck /> : <FiHeart />}
      {wishlisted
        ? t('productDetail.onWishlist', { defaultValue: '✓ On your wishlist — we’ll notify you' })
        : isUnavailableCombo
          ? t('productDetail.notifyUnavailable', { defaultValue: 'Notify me if this becomes available' })
          : t('productDetail.addWishlist', { defaultValue: 'Add to wishlist — get notified when back' })}
    </button>
  );

  // Spawn a small clone of the active product image and animate it into the
  // sticky cart bar. The cart bar pulses when the clone "lands" so the
  // customer sees a continuous trail from product → cart.
  /** Flies the product's picture to the bag; true when it did, false when there was nothing to fly from. */
  const spawnFlyToCart = () => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      window.dispatchEvent(new CustomEvent('stylon:cart-pulse'));
      return false;
    }
    const reduced = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const sourceEl = heroRef.current?.querySelector(`.${styles.carousel} img`)
      ?? heroRef.current?.querySelector('img');
    const sourceRect = sourceEl?.getBoundingClientRect();
    if (reduced || !sourceRect || sourceRect.width === 0) {
      window.dispatchEvent(new CustomEvent('stylon:cart-pulse'));
      return false;
    }
    const targetEl = document.querySelector('[data-testid="cart-top-floating"]')
      ?? document.querySelector('[data-testid="cart-sticky-bar"]')
      ?? document.querySelector(`.${styles.addToCartBtn}`);
    const targetRect = targetEl?.getBoundingClientRect();
    const fromX = sourceRect.left + sourceRect.width / 2;
    const fromY = sourceRect.top + sourceRect.height / 2;
    const toX = targetRect ? targetRect.left + targetRect.width / 2 : window.innerWidth / 2;
    const toY = targetRect ? targetRect.top + targetRect.height / 2 : window.innerHeight - 48;
    const size = Math.min(Math.max(sourceRect.width * 0.55, 96), 220);
    const id = `fly-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setFlyClone({
      id,
      src: sourceEl.currentSrc || sourceEl.src,
      size,
      fromX, fromY, toX, toY,
    });
    // Land + pulse, then remove clone.
    flyCleanupTimers.current.push(setTimeout(() => {
      window.dispatchEvent(new CustomEvent('stylon:cart-pulse'));
    }, 680));
    flyCleanupTimers.current.push(setTimeout(() => {
      setFlyClone((c) => (c?.id === id ? null : c));
    }, 820));
    return true;
  };

  const bumpQty = (dir) => {
    if (dir === 'inc' && (isOutOfStock || (tracks && qty >= stock))) return;
    setQtyBump(dir);
    setTimeout(() => setQtyBump(null), 260);
    if (dir === 'dec') setQty((q) => Math.max(1, q - 1));
    else setQty((q) => q + 1);
  };

  const salonLogoUrl = salon ? toImageUrl(salon.logo || salon.logoUrl) : null;
  // The shop card is the reference's cover-backed one, so it needs the cover
  // as well as the mark. dk-store has NEITHER set — `logoUrl` and
  // `coverImageUrl` both come back null from `/v1/public/salons/dk-store` —
  // which is exactly why the card must degrade to something composed rather
  // than to an empty grey box.
  const salonCoverUrl = salon
    ? toImageUrl(salon.coverImageUrl || salon.cover_image_url || salon.coverPhoto)
    : null;

  // Per-shop toggle: when off, ratings are hidden from the public page
  // (customers can still send a private review from their order). Default-on.
  const reviewsPublic = salon?.productReviewsPublic !== false;

  // Bottom variant picker for the fullscreen viewer — the active axis: colour
  // when present, otherwise the Type/style axis (where name-only variants land).
  // The same widget as the page's picker (round C: S2): every value a picture when the choice draws tiles, none when it
  // draws words. A value with no picture of its own: TEXT, the product's picture; COLOR: swatch — its hex, else a
  // neutral ring with its letter (fix round 1 #2, fix round 2 #2).
  const firstImageOf = (key, val) => ((key === 'color' ? colourWidget : styleWidget) === 'tiles'
    ? (tilePicture(product, key, val, key === 'color' ? sharedColour : sharedStyle) || null)
    : null);
  const viewerPicker = colorOptions.length > 1
    ? {
      optionLabel: axisName('color', 0, 'productDetail.color', 'Color'),
      selectedLabel: pickedColor && !isPending('color') ? colorLabel(pickedColor) : '',
      options: colorOptions.map((c) => ({
        key: c,
        label: colorLabel(c),
        // The value's own hex, else its name as CSS knows it; else the viewer draws a neutral ring with its letter.
        swatch: swatchColour(product, 'color', c),
        letter: swatchLetter(colorLabel(c)),
        thumb: firstImageOf('color', c),
        selected: pickedColor === c && !isPending('color'),
        onSelect: () => choose('color', c),
      })),
    }
    : styleOptions.length > 1
      ? {
        optionLabel: axisName('style', 2, 'productDetail.style', 'Type'),
        selectedLabel: (!isPending('style') && pickedStyle && valueLabel('style', pickedStyle)) || '',
        options: styleOptions.map((s) => ({
          key: s,
          label: valueLabel('style', s),
          swatch: null,
          letter: swatchLetter(valueLabel('style', s)),
          thumb: firstImageOf('style', s),
          selected: pickedStyle === s && !isPending('style'),
          onSelect: () => choose('style', s),
        })),
      }
      : { optionLabel: '', selectedLabel: '', options: [] };
  /* The outline and the one line, on the axis a tap on add-to-cart is waiting
   * for. The line sits only on the FIRST such axis, so it is said once. */
  const askClass = (axis) => (choiceAsked && isPending(axis) ? styles.variantSectionAsk : '');
  const choiceNoteFor = (axis) => (choiceAsked && pendingAxes[0] === axis ? (
    <p className={styles.choiceNote} role="alert" data-testid="product-detail-choice-note">
      <FiAlertCircle className={styles.choiceNoteIcon} aria-hidden />
      {t('productDetail.chooseOption', {
        defaultValue: 'Please choose: {{axis}}',
        axis: axisName(axis, { color: 0, size: 1, style: 2 }[axis], `productDetail.${axis}`, { color: 'Color', size: 'Size', style: 'Type' }[axis]),
      })}
    </p>
  ) : null);

  /* AN OPEN PICK ASKS FOR ITS NOTE (Swe Sosetta run 4, round B: S7). «نكهة وحدة» leaves the flavour to the buyer, who
   * had no way to say which: once it is chosen, the builder's prompt (W6 `valueMeta.notePrompt`) is said under the
   * choice — «اكتبوا النكهة اللي بدكم إياها بملاحظة الطلب» — and the order note and the WhatsApp message leave the blank. */
  const notePromptLine = (axis) => {
    if (isPending(axis)) return null;
    const value = axis === 'color' ? pickedColor : (axis === 'size' ? pickedSize : pickedStyle);
    const prompt = notePromptOf(product, axis, value, i18n.language);
    return prompt ? (
      <p className={styles.notePrompt} data-testid="product-detail-note-prompt">
        <FiEdit3 className={styles.notePromptIcon} aria-hidden />
        {prompt}
      </p>
    ) : null;
  };

  /* What the add button says: "added" only when something was, the stock
   * ceiling when the whole shelf is already in the cart (UX-SHOP-15). */
  const capNoteText = capNote != null
    ? t('productDetail.allInCart', { defaultValue: 'All {{n}} in stock are already in your cart', n: capNote })
    : null;
  /* THE BUTTON ASKS FOR THE CHOICE BEFORE THE TAP.
   *
   * A product sold in colours cannot go in the cart until one is picked, and the button used to say "Add to cart"
   * whatever was missing: the tap did nothing but scroll, and only then said what to choose. Of 360 people who came
   * to DK's luggage from the ads in the week to 21 Sept 2026, 26 ever touched a colour. So the button names what is
   * missing while it is missing — "Choose a colour" — and the tap still takes them to it. */
  const pendingAxisName = pendingAxes.length > 0
    ? axisName(pendingAxes[0], { color: 0, size: 1, style: 2 }[pendingAxes[0]], `productDetail.${pendingAxes[0]}`, { color: 'Color', size: 'Size', style: 'Type' }[pendingAxes[0]])
    : null;
  /* The maker's name in the reader's language, only on a product that HAS a brand (`brand` is `null` on an
   * unbranded product, never absent — see ProductBrandRow). */
  const brandEyebrow = (product?.brand?.id || product?.brand?.name) ? (brandName(product.brand, i18n?.language) || null) : null;
  let addToCartLabel = t('productDetail.addToCart', { defaultValue: 'Add to cart' });
  if (capNoteText) addToCartLabel = capNoteText;
  else if (justAdded) addToCartLabel = t('productDetail.addedToCart', { defaultValue: 'Added' });
  else if (pendingAxisName && !isOutOfStock) {
    addToCartLabel = t('productDetail.chooseFirst', { defaultValue: 'Choose {{axis}}', axis: pendingAxisName });
  }

  /* ONE BUTTON ON THE SCREEN NAMES THE MISSING CHOICE, AND QUICK BUY IS ALWAYS IN THE PAGE (run 4, round B: S5; run 5,
   * round C: S1, S1b; the trial gate, 10-29). The outlined quick buy said «اختاروا النكهة» while the add under the same
   * eyes said «أضف إلى السلة / اختاروا النكهة» — two prompts for one choice. Round C hid quick buy on a phone to fix
   * that, and so took the in-page button away from the phone shopper it was put there for: the owner's 2026-10-08 rule
   * is "the button in the page, not in the docked area of actions", and DK's ad shoppers buy through it (e2e 92 walks
   * it inside Facebook's and Instagram's browsers). Three rules, all kept:
   *   - quick buy is in the page on every width and names the missing choice; its tap opens the sheet to buy (10-08);
   *   - a door the shopper can see names what is missing, never looks ready (the store team, 10-08: of 360 people who
   *     reached DK's luggage from the ads, 26 ever touched a colour);
   *   - one control on the screen asks (round C). So the docked add carries the choice as its hint line only while
   *     quick buy is off the screen (`quickBuyOnScreen`); while quick buy shows, the add says its own word. It keeps the
   *     primary (orange) look — it leads the bar (10-08) — and its tap asks through the sheet either way.
   * A desktop has no docked bar: quick buy names the choice, and the inline add says its word and looks waiting (grey). */
  const waitingForChoice = !!pendingAxisName && !isOutOfStock && !justAdded && !capNoteText;
  const dockedAsks = waitingForChoice && !quickBuyOnScreen;
  /* An add that does not ask says, and is named, its own word (WCAG 2.5.3). Beside «عرض السلة» the docked add says the
   * short «أضف». */
  const addWord = waitingForChoice ? t('productDetail.addToCart', { defaultValue: 'Add to cart' }) : addToCartLabel;
  const dockedDoor = !leanCheckout && cartCount > 0 && !justAdded ? t('productDetail.addToCartShort', { defaultValue: 'Add' }) : addWord;
  /* The docked add's name while it asks: the word it shows, then the choice it waits for (WCAG 2.5.3 — the visible
   * label first). It is not disabled: its tap asks for the choice and opens the sheet (fix round 3). */
  const dockedAddName = dockedAsks
    ? t('productDetail.addWaitingName', {
      defaultValue: '{{door}}, {{choice}}',
      door: t('productDetail.addToCart', { defaultValue: 'Add to cart' }),
      choice: addToCartLabel,
    })
    : addWord;
  /* The add's own word with the choice under it. `door` is the word the button shows — beside «عرض السلة» the short
   * «أضف» — and the hint is the missing choice either way (round C: S1). */
  const waitingWordsFor = (door) => (
    <span className={styles.addWaitingWords}>
      <span className={styles.addWaitingDoor} data-label>{door}</span>
      <span className={styles.addWaitingHint} data-hint>{addToCartLabel}</span>
    </span>
  );

  const viewerPrimaryLabel = isOutOfStock
    ? (wishlisted
      ? t('productDetail.onWishlistShort', { defaultValue: 'On your wishlist' })
      : t('productDetail.addWishlistShort', { defaultValue: 'Add to wishlist' }))
    : t('productDetail.addToCart', { defaultValue: 'Add to cart' });
  const viewerPrimaryIcon = isOutOfStock
    ? (wishlisted ? <FiCheck /> : <FiHeart />)
    : <FiShoppingCart />;
  const viewerOnPrimary = isOutOfStock
    ? toggleWishlist
    : () => { handleAddToCart('add'); setViewerOpen(false); };

  return (
    <div className={styles.page} data-testid="product-detail-page" dir={isRtl ? 'rtl' : 'ltr'}>
      {/* THE PAGE'S HEADER: back, the product's name once its photograph is gone, save and share.
        * Save and share used to float on the photograph itself (2026-10-07, earlier the same
        * day). The owner: "the love and share aren't in a good place, should've be aligned with
        * the back button or something" — and they were right twice over, because on the
        * photograph they also had to be glass discs that fight whatever is behind them. Here
        * they are ordinary buttons on the page's own surface, level with Back, and the
        * photograph is left to the product. */}
      <div ref={topBarRef} className={`${styles.topBar} ${heroGone ? styles.topBarNamed : ''}`} data-testid="product-detail-top-bar">
        <button
          type="button"
          className={styles.topBackBtn}
          aria-label={t('common.back', { defaultValue: 'Back' })}
          onClick={() => {
            // From an ad there IS no previous page in the shop, and navigate(-1)
            // walks the person back out to Facebook. See backFromHere.js.
            const back = backFromHere({
              locationKey: location?.key,
              basePath: getBasePath(salonSlug),
              brand: product?.brand,
            });
            if (back.kind === 'to') navigate(back.to);
            else navigate(-1);
          }}
          data-testid="product-detail-back"
        >
          <BackIcon />
        </button>
        <span className={styles.topBarTitle} data-testid="product-detail-top-bar-title" aria-hidden={!heroGone}>
          {heroGone ? product.name : ''}
        </span>
        {/* THE SHARE SITS WITH THE HEART, IN THE BAR (2026-10-12, the owner: "the share button can be better"). It
            stood alone in the page as a pill with its word, under quick buy; up here it is the heart's twin, one round
            button beside the other, where a phone's own share lives. */}
        {actionsOnHero && renderTitleActions('top', ['like', 'share'])}
      </div>

      {freeShippingThreshold > 0 && (
        <FreeShippingBar
          threshold={freeShippingThreshold}
          cartTotal={cartTotal}
          currencySymbol={currencySymbol(currency)}
          testId="product-free-shipping-bar"
        />
      )}

      <div
        ref={heroRef}
        className={styles.heroBlock}
        data-testid="product-detail-hero"
      >
      <div
        className={`${styles.gallery} ${images.length > 1 ? "" : styles.gallerySolo}`}
        data-testid="product-detail-gallery"
      >
        <div
          ref={stageRef}
          className={styles.carousel}
          data-testid="product-detail-carousel"
          onClick={() => {
            // A drag that crossed the threshold ends in a `click` too. Swallow
            // exactly that one, or every drag finishes in the viewer.
            if (suppressClickRef.current) { suppressClickRef.current = false; return; }
            galleryStep('viewer', 'viewer_open', galleryIndex);
            setViewerOpen(true);
          }}
          role="button"
          tabIndex={0}
          aria-label={t('productDetail.openGallery', { defaultValue: 'Open photos' })}
          onKeyDown={(e) => {
            if (handleStageKeyDown(e)) return;
            if (e.key === 'Enter') { galleryStep('viewer', 'viewer_open', galleryIndex); setViewerOpen(true); }
          }}
          onWheel={handleStageWheel}
          onMouseDown={onStageMouseDown}
          onMouseMove={onStageMouseMove}
          onMouseUp={endStageDrag}
          onMouseLeave={endStageDrag}
          style={{ cursor: 'zoom-in' }}
        >
          {stageBackdropUrl && (
            <div
              className={styles.stageBackdrop}
              style={{ backgroundImage: `url("${stageBackdropUrl}")` }}
              aria-hidden
              data-testid="product-detail-stage-backdrop"
            />
          )}
          {/* `fitContain` — the stage is a fixed 1 : 1.25 box and the
            * photograph is FITTED into it, never cut. See the note on
            * `.carousel` for the count that decided the ratio: this
            * catalogue is 55% square and 33% at 1 : 1.5, so no single
            * cropping stage can be right for it and none is asked to be.
            * The prop already exists on this shared component — the
            * full-screen viewer uses it — so nothing inside the carousel
            * changes, which matters: it draws every card in the shop grid. */}
          <ProductCarousel
            onFirstPhotoLoad={() => setHeroReady(true)}
            images={images}
            alt={product.name}
            counter
            fitContain
            /* …each picture in a box of its own shape, on the plain surface, the blur only around it (S4 fix round 2). */
            framed
            onPictureUnsized={notePictureUnsized}
            /* And the letterbox has to be the STAGE's ground, not the shared
             * component's. `.wrap` paints `#f1f5f9` — a cold blue-grey for a
             * white card — and with `contain` that grey is now visible on
             * every side of every photograph, sitting on a warm peach page.
             * `transparentBg` is the prop that already exists for exactly
             * this (the full-screen viewer uses it to let its black sheet
             * through), so `.carousel`'s `--shop-plate` shows instead. */
            transparentBg
            index={galleryIndex}
            onIndexChange={setGalleryIndex}
            onShopperMove={(i, action) => galleryStep('product', action, i)}
            videoAutoplay
          />
          {/* THE CONTROLS THE REFERENCE LEAVES OUT AND A DESKTOP STILL NEEDS.
            *
            * shop.app navigates its desktop gallery by thumbnail click alone —
            * measured: a wheel over its stage does nothing, a drag does
            * nothing. That is defensible with nine photographs in a rail that
            * shows all nine. This shop's rows carry up to 42, so the rail is a
            * scroller and "click the one you want" stops being an answer for
            * the photograph immediately after the one you are looking at.
            *
            * They sit INSIDE the stage and are `type="button"`, so the click
            * that pages the gallery does not also reach the stage's own
            * open-the-viewer handler — `stopPropagation` on each. */}
          {images.length > 1 && (
            <>
              <button
                type="button"
                className={`${styles.galleryNavBtn} ${styles.galleryNavPrev}`}
                data-testid="product-detail-gallery-prev"
                disabled={galleryIndex <= 0}
                aria-label={t('productDetail.previousPhoto', { defaultValue: 'Previous photo' })}
                onClick={(e) => { e.stopPropagation(); stepImage(-1); }}
              >
                {isRtl ? <FiChevronRight aria-hidden /> : <FiChevronLeft aria-hidden />}
              </button>
              <button
                type="button"
                className={`${styles.galleryNavBtn} ${styles.galleryNavNext}`}
                data-testid="product-detail-gallery-next"
                disabled={galleryIndex >= images.length - 1}
                aria-label={t('productDetail.nextPhoto', { defaultValue: 'Next photo' })}
                onClick={(e) => { e.stopPropagation(); stepImage(1); }}
              >
                {isRtl ? <FiChevronLeft aria-hidden /> : <FiChevronRight aria-hidden />}
              </button>
            </>
          )}
          {pictureOfCaption && (
            <p className={styles.pictureOf} data-testid="product-detail-picture-of" aria-live="polite">
              <bdi>{pictureOfCaption}</bdi>
            </p>
          )}
        </div>
        {/* WHERE THE SHOPPER IS, SAID OUT LOUD.
          *
          * The "1 / 8" pill inside the carousel is `aria-hidden` — it is a
          * decoration painted over a photograph. Someone driving this gallery
          * from the keyboard gets no feedback at all from an arrow key
          * otherwise: the picture changes and nothing is announced. This is
          * the same sentence, visually hidden, in the customer's language. */}
        {images.length > 1 && (
          <p
            className={styles.galleryPosition}
            data-testid="product-detail-gallery-position"
            aria-live="polite"
            aria-atomic="true"
          >
            {t('productDetail.imageNofM', {
              defaultValue: 'Image {{index}} of {{total}}',
              index: galleryIndex + 1,
              total: images.length,
            })}
          </p>
        )}
        {images.length > 1 && (
          <div
            ref={railRef}
            className={styles.thumbStrip}
            role="tablist"
            aria-orientation="vertical"
            aria-label={t('productDetail.imageThumbnails', { defaultValue: 'Image thumbnails' })}
            data-testid="product-detail-thumbs"
          >
            {images.map((img, i) => (
              <button
                key={img.id || img.url || i}
                type="button"
                role="tab"
                aria-selected={i === galleryIndex}
                className={`${styles.thumb} ${i === galleryIndex ? styles.thumbActive : ''}`}
                onClick={() => { if (i !== galleryIndex) galleryStep('product', 'thumb', i); setGalleryIndex(i); }}
                data-testid={`product-detail-thumb-${i}`}
              >
                {isVideoUrl(img.url) ? (
                  <span className={styles.thumbVideoWrap}>
                    <video src={img.url} muted playsInline preload="metadata" />
                    <FiPlay className={styles.thumbPlayBadge} aria-hidden />
                  </span>
                ) : (
                  <img src={renditionUrl(img.url, 480)} alt="" loading="lazy" fetchpriority="low" decoding="async" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>


      {/* ONLY ON A PHONE. On a desktop `.heroBlock` is a two-column grid and `.gallery` is
        * `display: contents`, so its children ARE the grid's children — and an extra one here
        * takes a cell and pushes the info column onto a second row. That is exactly what it
        * did: the picture sat in the right-hand column with the whole left column empty and
        * the name, price and buy button stranded underneath. The header only needs this marker
        * on a phone, where it is the thing that becomes a named bar. */}
      {actionsOnHero && <span ref={heroEndRef} className={styles.heroSeam} aria-hidden />}

      <div className={styles.info} data-testid="product-detail-info">
        <div className={styles.titleRow}>
          <div className={styles.titleBlock}>
            {/* THE BRAND IS THE FIRST SMALL LINE (the owner, 2026-10-06: "first line make it the brand (small
              * one) also in the product page") — the way the reference and the basket lead with the maker, from
              * the same `product.brand` row and the same `brandName` the basket line carries (`snapshot.brand`).
              * The brand row further down stays: it is the way INTO the brand's catalogue; this is the name. */}
            {/* THE BRAND LINE CARRIES THE DISCOUNT (2026-10-07). The owner: "the label of the
              * discount is too too big and not nice. how it was before? maybe we can do it as
              * the one in the design I've provided next to the name line or something?" — and
              * the reference does exactly that: the category on one side of the first small
              * line, a coloured pill on the other. On the photograph it had to be big enough
              * to survive an unknown image and had to dodge Back, save, share and the counter;
              * here it is a chip on a white card, so it can be small and simply red. */}
            {/* "WHY NOT WE HAVING THE OFFICIAL IMPORTER V SOMEWHERE... maybe instead of badge at
              * the top or next to the brand line name" (2026-10-07, with a reference whose
              * "★ BEST SELLER" pill sits on the small line above the product's name).
              *
              * It goes on the brand's own line because that is what it is ABOUT — this shop is
              * the official importer OF this brand, and the claim beside any other word would
              * be floating. The page already stated it, 1,100px further down in the brand row;
              * a shopper deciding on the price never sees that.
              *
              * The shared OfficialImporterTick, not a new mark: this storefront had the flag
              * drawn four different ways before that component existed, and the note at the
              * top of it is explicit that "everywhere" means one component. `decorative`,
              * because the words are right beside it — the disc is punctuation on the claim,
              * not a second statement of it.
              *
              * Inside the eyebrow <p> rather than beside it: `theBrandIsTheFirstSmallLine`
              * holds the brand to being the title block's first child and a direct sibling of
              * the H1, and a wrapper around the pair breaks that (it already did, once today). */}
            {brandEyebrow && (
              <p className={styles.brandEyebrow} data-testid="product-detail-brand-eyebrow" data-brand-id={product.brand?.id ?? undefined}>
                {brandEyebrow}
              </p>
            )}
            {/* THE CHIPS SIT BESIDE THE BRAND, NOT INSIDE IT. The importer mark went inside the
              * eyebrow first and `theBrandIsTheFirstSmallLine` caught it the same way the
              * wrapper was caught an hour earlier — that guard compares the eyebrow's whole
              * textContent with the brand the BASKET stores, so that a line in the checkout
              * can lead with the same words. Putting a second claim in that element made the
              * page say "GANTOfficial importer" where the basket says "GANT". The eyebrow
              * holds the brand name and nothing else; the chips are pinned next to it. */}
            {product.brand?.isOfficialImporter === true && (
              <span className={styles.importerMark} data-testid="product-detail-brand-importer-eyebrow">
                <OfficialImporterTick t={t} decorative className={styles.importerMarkTick} />
                <span>{t('shop.officialImporter', { defaultValue: 'Official importer' })}</span>
              </span>
            )}
            {discounted && (
              <span className={styles.discountPill} data-testid="product-detail-image-discount">
                {/* Isolated LTR: "35%", never "% 35" in a Hebrew line. */}
                <span dir="ltr">{discountPct}%</span>
              </span>
            )}
            <h1 className={styles.productName}>{product.name}</h1>
            {selectedVariant?.name && selectedVariant.name !== selectedVariant.style && (
              <p className={styles.variantHint}>{selectedVariant.name}</p>
            )}
            {/* THE PRODUCT'S RATING, WHERE THE REFERENCE PUTS IT.
              *
              * Measured on `shop.app/m/johnelliott`: five 16px stars and the
              * words "7 ratings", 4px under the H1 and ABOVE the price. Not a
              * repeated numeral (the reviews block below already prints the
              * average at 36px) and not a chevron — the whole row is the
              * control, and it goes to that block.
              *
              * `StarsRow` is the SHARED strip, so this page has one answer to
              * "what does a star look like" instead of the two it had: a
              * hand-rolled `#FFC00A` loop here and the page-derived token
              * fifteen hundred pixels below. */}
            {reviewsPublic && reviewSummary.count > 0 && (
              <button
                type="button"
                className={styles.reviewsSummary}
                onClick={() => reviewsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                data-testid="product-detail-reviews-summary"
              >
                <StarsRow
                  value={reviewSummary.average}
                  size={16}
                  t={t}
                  testId="product-detail-reviews-summary-stars"
                />
                <span className={styles.reviewsCount}>
                  {t('reviews.ratingsCount', {
                    defaultValue: '{{n}} ratings',
                    n: reviewSummary.count,
                    count: reviewSummary.count,
                  })}
                </span>
              </button>
            )}
          </div>
        </div>

        <div className={styles.priceRow}>
          {/* The row's change, said once: a hidden polite region carries its text; the visible row is not live itself. */}
          <span className={styles.srOnly} aria-live="polite" aria-atomic="true" data-testid="product-detail-price-live">
            {noPrice ? '' : `${priceFrom != null ? `${fromWord} ` : ''}${groupedShopAmount(priceFrom != null ? priceFrom.price : price, currency)} ${currencySymbol(currency)}`}
          </span>
          {noPrice ? (
            /* Nothing to show: ₪0 on a giveaway is a promise nobody made. */
            <span className={styles.askForPrice} data-testid="product-detail-ask-price">
              {t('productDetail.askForPrice', { defaultValue: 'Ask the shop for the price' })}
            </span>
          ) : priceFrom != null ? (
            /* The word stays outside the price's left-to-right run, so «من» reads before «65 ₪» in a right-to-left line.
             * The struck price is the cheapest row's, as the card the shopper came from strikes it. */
            <span className={`${styles.priceFrom} ${/[\u05BE-]$/.test(fromWord) ? styles.priceFromJoined : ''}`} data-testid="product-detail-price-from">
              <span className={styles.priceFromWord}>{fromWord}</span>
              <PriceTag amount={priceFrom.price} currency={currency} className={styles.price} testId="product-detail-price" />
            </span>
          ) : (
            <PriceTag amount={price} currency={currency} className={styles.price} testId="product-detail-price" />
          )}
          {!noPrice && (priceFrom ? priceFrom.discounted : discounted) && (
            <PriceTag amount={priceFrom ? priceFrom.compareAt : compareAt} currency={currency} className={styles.compareAt} testId="product-detail-price-compare" />
          )}
          {/* Stock badge only when it matters: out of stock, or running low
           * ("about to end"). Plenty-in-stock shows nothing. */}
          {asks ? null : isUnavailableCombo ? (
            <span
              data-testid="product-detail-stock-badge"
              className={`${styles.stockBadge} ${styles.stockBadgeOut}`}
            >
              {t('productDetail.comboUnavailable', { defaultValue: 'Combination unavailable' })}
            </span>
          ) : tracks && stock <= 0 ? (
            <span
              data-testid="product-detail-stock-badge"
              className={`${styles.stockBadge} ${styles.stockBadgeOut}`}
            >
              {t('productDetail.outOfStock', { defaultValue: 'Out of stock' })}
            </span>
          ) : tracks && stock < 5 ? (
            <span
              data-testid="product-detail-stock-badge"
              className={`${styles.stockBadge} ${styles.stockBadgeLow}`}
            >
              <StatusDot level="warning" pulse />
              {t('productDetail.lowStock', { defaultValue: 'Only {{n}} left', n: stock })}
            </span>
          ) : null}
          {/* THE SHOP'S REVIEWS, AS A NUMBER, WHERE THE PRICE IS.
            * "★ 5.0 · 86 reviews on Google" — the block that holds them sits
            * about 1,600px down a phone page and the price is at about 450,
            * so on dk-store the one piece of social proof this shop has was
            * below everything anybody reads. The chip is the number only; a
            * tap opens the merged reviews view. It paints from the salon
            * payload the page already holds, so it costs no request and it
            * renders nothing when the shop has no reviews. */}
          <ShopReviewsPriceChip
            shopReviews={salon?.shopReviews}
            salonSlug={salonSlug}
            shopName={salon?.name}
            t={t}
          />
        </div>
        {/* WHOLESALE FOR BIG ORDERS (2026-10-25): a priced tray keeps its price and its cart, and says that big orders
          * are priced by asking — with the same ask under it. An ask product already leads with the ask, so it keeps
          * the line alone. */}
        {product.bulkPricesOnRequest === true && (
          <>
            <p className={styles.bulkLine} data-testid="product-detail-bulk-line">
              {t('productDetail.bulkPricesOnRequest', { defaultValue: 'Wholesale prices for big orders — contact us' })}
            </p>
            {!asks && renderContactOrder('product-detail-bulk', 'bulk', { ask: true, quiet: true, rowRef: setInlineAskEl })}
          </>
        )}
        {/* THE CHOICE STANDS BESIDE THE PRICE IT CHANGES — above the sale box, not
          * under it, where a 390px screen left it below the fold with the sticky
          * add button already live (DK Store, KOALA set, 2026-09-16). */}
        <div ref={pickerRef} data-testid="product-detail-variant-picker">
          {colorOptions.length >= 1 && (
            <div
              className={`${styles.variantSection} ${askClass('color')}`}
              data-testid="product-detail-color-selector"
              data-axis="color"
            >
              {colorOptions.length > 1 ? (
              <>
              <button
                type="button"
                className={styles.variantTitleInline}
                onClick={() => setColorModalOpen(true)}
                data-testid="product-detail-color-title"
              >
                <span className={styles.variantTitleLabel}>{axisName('color', 0, 'productDetail.color', 'Color')}</span>
                {!isPending('color') && (
                  <span className={styles.variantTitleValue}>{colorLabel(pickedColor)}</span>
                )}
                <ForwardChevron className={styles.variantTitleChevron} aria-hidden />
              </button>
              {choiceNoteFor('color')}
              {/* ONE WIDGET PER CHOICE, IN THE PAYLOAD'S ORDER (Swe Sosetta run 5, round C: S2; fix round 1, #2, #5). Run
                * 5's mini cookies drew the one flavour with a photo of its own as a tile and the other two as pills, tile
                * first. Now, if any value has a picture of its own, every value is a tile; otherwise every value is a
                * pill (panel S5: no borrowed picture reads as "this is what Lotus looks like" on its own). A tile for a
                * value with no photo of its own depends on the choice's type (utils/optionPictures tilePicture):
                *   - TEXT (a bakery's flavours, designs): the product's picture, with the value's name under it;
                *   - COLOR: its colour swatch — and a colour that shares a photo with another colour keeps that photo.
                * The order is the server's (options by display order); the page adds none of its own. */}
              {colourWidget === 'tiles' ? (
              <div className={styles.colorThumbRow} data-testid="product-detail-color-tiles">
                {colorOptions.map((c) => {
                  const state = optionAvailability.color[c] || 'available';
                  const disabled = state !== 'available';
                  // A colour with no photo of its own draws its swatch, never the gallery's first photo (fix round 1, #2).
                  const imgUrl = tilePicture(product, 'color', c, sharedColour) || (isColourChoice(product, 'color') ? null : images?.[0]?.url) || null;
                  const selected = pickedColor === c && !isPending('color');
                  return (
                    <button
                      key={c}
                      type="button"
                      className={`${styles.colorThumb} ${selected ? styles.colorThumbSelected : ''} ${disabled ? styles.colorThumbDisabled : ''}`}
                      title={colorLabel(c)}
                      aria-label={colorLabel(c)}
                      aria-pressed={selected}
                      aria-disabled={disabled}
                      data-testid={`product-detail-color-${c.toLowerCase()}`}
                      aria-describedby={choicePriceId('color', c)}
                      onClick={(e) => {
                        choose('color', c);
                        e.currentTarget.blur();
                      }}
                    >
                      <span className={styles.colorThumbImgWrap}>
                        {imgUrl
                          ? <img src={heroReady ? renditionUrl(imgUrl, 480) : undefined} data-photo={renditionUrl(imgUrl, 480)} alt="" className={styles.colorThumbImg} loading="lazy" fetchpriority="low" decoding="async" />
                          : axisIsColorSwatch(0)
                            ? (() => {
                              // Painted from the value's own hex, else its name; neither → a neutral ring with its first
                              // letter, never an empty disc (fix round 2, #2).
                              const fill = swatchColour(product, 'color', c);
                              return fill
                                ? <span className={styles.colorThumbSwatch} style={{ background: fill }} data-swatch="colour" aria-hidden />
                                : <span className={`${styles.colorThumbSwatch} ${styles.swatchNeutral}`} data-swatch="neutral" aria-hidden>{swatchLetter(colorLabel(c))}</span>;
                            })()
                            : <span className={styles.colorThumbSwatch} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600 }} aria-hidden>{colorLabel(c)}</span>}
                      </span>
                      <TileName label={colorLabel(c)} />
                      <ChoicePrice id={choicePriceId('color', c)} amount={choicePriceOf('color', c)} currency={currency} />
                    </button>
                  );
                })}
              </div>
              ) : (
                <div className={styles.swatchRow} data-testid="product-detail-color-words">
                  {colorOptions.map((c) => {
                    const disabled = (optionAvailability.color[c] || 'available') !== 'available';
                    const selected = pickedColor === c && !isPending('color');
                    return (
                      <button
                        key={c}
                        type="button"
                        className={`${styles.chip} ${selected ? styles.chipSelected : ''} ${disabled ? styles.chipDisabled : ''}`}
                        aria-pressed={selected}
                        aria-disabled={disabled}
                        data-testid={`product-detail-color-${c.toLowerCase()}`}
                        aria-describedby={choicePriceId('color', c)}
                        onClick={(e) => {
                          choose('color', c);
                          e.currentTarget.blur();
                        }}
                      >
                        <bdi>{colorLabel(c)}</bdi>
                        <ChoicePrice id={choicePriceId('color', c)} amount={choicePriceOf('color', c)} currency={currency} />
                      </button>
                    );
                  })}
                </div>
              )}
              {notePromptLine('color')}
              </>
              ) : (
                <>
                <h3 className={styles.variantSectionTitle} data-testid="product-detail-color-single">
                  {axisName('color', 0, 'productDetail.color', 'Color')}
                  <span className={styles.variantSectionValue}>
                    {/* The dot only when there is a colour to paint it (fix round 2, #2): the name follows anyway. */}
                    {axisIsColorSwatch(0) && swatchColour(product, 'color', pickedColor) && (
                      <span
                        aria-hidden
                        data-testid="product-detail-color-single-dot"
                        style={{
                          display: 'inline-block', width: 14, height: 14, borderRadius: '50%',
                          background: swatchColour(product, 'color', pickedColor),
                          border: '1px solid rgba(0,0,0,0.15)', marginInlineEnd: 6, verticalAlign: '-2px',
                        }}
                      />
                    )}
                    {colorLabel(pickedColor)}
                  </span>
                </h3>
                {notePromptLine('color')}
                </>
              )}
            </div>
          )}

          {/* Both size + type are fixed (no choice) → show them together on one
           * line to save vertical space. */}
          {sizeOptions.length === 1 && styleOptions.length === 1 && (
            <div className={styles.variantSection} data-testid="product-detail-specs">
              <div className={styles.singleSpecRow}>
                <span className={styles.singleSpec}>
                  <b>{axisName('size', 1, 'productDetail.size', 'Size')}</b><bdi>{valueLabel('size', pickedSize)}</bdi>
                </span>
                <span className={styles.singleSpec}>
                  <b>{axisName('style', 2, 'productDetail.style', 'Type')}</b><bdi>{valueLabel('style', pickedStyle)}</bdi>
                </span>
              </div>
            </div>
          )}

          {sizeOptions.length >= 1 && !(sizeOptions.length === 1 && styleOptions.length === 1) && (
            <div
              className={`${styles.variantSection} ${askClass('size')}`}
              data-testid="product-detail-size-selector"
              data-axis="size"
            >
              <h3 className={styles.variantSectionTitle}>
                {axisName('size', 1, 'productDetail.size', 'Size')}
                {!isPending('size') && <span className={styles.variantSectionValue}><bdi>{valueLabel('size', pickedSize)}</bdi></span>}
              </h3>
              {choiceNoteFor('size')}
              {sizeOptions.length > 1 && (
                <div className={styles.swatchRow}>
                  {sizeOptions.map((s) => {
                    const state = optionAvailability.size[s] || 'available';
                    const disabled = state !== 'available';
                    return (
                      <button
                        key={s}
                        type="button"
                        className={`${styles.chip} ${pickedSize === s && !isPending('size') ? styles.chipSelected : ''} ${disabled ? styles.chipDisabled : ''}`}
                        aria-pressed={pickedSize === s && !isPending('size')}
                        aria-disabled={disabled}
                        data-testid={`product-detail-size-${s}`}
                        aria-describedby={choicePriceId('size', s)}
                        onClick={(e) => {
                          choose('size', s);
                          e.currentTarget.blur();
                        }}
                      >
                        <bdi>{valueLabel('size', s)}</bdi>
                        <ChoicePrice id={choicePriceId('size', s)} amount={choicePriceOf('size', s)} currency={currency} />
                      </button>
                    );
                  })}
                </div>
              )}
              {notePromptLine('size')}
            </div>
          )}

          {styleOptions.length >= 1 && !(sizeOptions.length === 1 && styleOptions.length === 1) && (
            <div
              className={`${styles.variantSection} ${askClass('style')}`}
              data-testid="product-detail-style-selector"
              data-axis="style"
            >
              <h3 className={styles.variantSectionTitle}>
                {axisName('style', 2, 'productDetail.style', 'Type')}
                {!isPending('style') && <span className={styles.variantSectionValue}><bdi>{valueLabel('style', pickedStyle)}</bdi></span>}
              </h3>
              {choiceNoteFor('style')}
              {styleOptions.length > 1 && (
                colorOptions.length > 1 ? (
                  // Color is the visual axis → keep Type as compact text chips.
                  <div className={styles.swatchRow}>
                    {styleOptions.map((s) => {
                      const state = optionAvailability.style[s] || 'available';
                      const disabled = state !== 'available';
                      return (
                        <button
                          key={s}
                          type="button"
                          className={`${styles.chip} ${pickedStyle === s && !isPending('style') ? styles.chipSelected : ''} ${disabled ? styles.chipDisabled : ''}`}
                          aria-pressed={pickedStyle === s && !isPending('style')}
                          aria-disabled={disabled}
                          data-testid={`product-detail-style-${s.toLowerCase()}`}
                          aria-describedby={choicePriceId('style', s)}
                          onClick={(e) => {
                            choose('style', s);
                            e.currentTarget.blur();
                          }}
                        >
                          <bdi>{valueLabel('style', s)}</bdi>
                          <ChoicePrice id={choicePriceId('style', s)} amount={choicePriceOf('style', s)} currency={currency} />
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  // No color axis → Type IS the primary axis (this covers name-only
                  // variants, which are mapped to the Type axis). One widget for every type, in the payload's order
                  // (round C: S2): image cards (the type's photo, else the product's, and its label) when any type
                  // has a picture of its own, else words.
                  <>
                  {styleWidget === 'tiles' ? (
                  <div className={styles.colorThumbRow} data-testid="product-detail-style-tiles">
                    {styleOptions.map((s) => {
                      const state = optionAvailability.style[s] || 'available';
                      const disabled = state !== 'available';
                      const imgUrl = tilePicture(product, 'style', s, sharedStyle) || images?.[0]?.url || null;
                      const selected = pickedStyle === s && !isPending('style');
                      return (
                        <button
                          key={s}
                          type="button"
                          className={`${styles.colorThumb} ${selected ? styles.colorThumbSelected : ''} ${disabled ? styles.colorThumbDisabled : ''}`}
                          title={valueLabel('style', s)}
                          aria-label={valueLabel('style', s)}
                          aria-pressed={selected}
                          aria-disabled={disabled}
                          data-testid={`product-detail-style-${s.toLowerCase()}`}
                          aria-describedby={choicePriceId('style', s)}
                          onClick={(e) => {
                            choose('style', s);
                            e.currentTarget.blur();
                          }}
                        >
                          <span className={styles.colorThumbImgWrap}>
                            {imgUrl
                              ? <img src={heroReady ? renditionUrl(imgUrl, 480) : undefined} data-photo={renditionUrl(imgUrl, 480)} alt="" className={styles.colorThumbImg} loading="lazy" fetchpriority="low" decoding="async" />
                              : <span className={styles.colorThumbSwatch} aria-hidden />}
                          </span>
                          <TileName label={valueLabel('style', s)} />
                          <ChoicePrice id={choicePriceId('style', s)} amount={choicePriceOf('style', s)} currency={currency} />
                        </button>
                      );
                    })}
                  </div>
                  ) : (
                    <div className={styles.swatchRow} data-testid="product-detail-style-words">
                      {styleOptions.map((s) => {
                        const disabled = (optionAvailability.style[s] || 'available') !== 'available';
                        const selected = pickedStyle === s && !isPending('style');
                        return (
                          <button
                            key={s}
                            type="button"
                            className={`${styles.chip} ${selected ? styles.chipSelected : ''} ${disabled ? styles.chipDisabled : ''}`}
                            aria-pressed={selected}
                            aria-disabled={disabled}
                            data-testid={`product-detail-style-${s.toLowerCase()}`}
                            aria-describedby={choicePriceId('style', s)}
                            onClick={(e) => {
                              choose('style', s);
                              e.currentTarget.blur();
                            }}
                          >
                            <bdi>{valueLabel('style', s)}</bdi>
                            <ChoicePrice id={choicePriceId('style', s)} amount={choicePriceOf('style', s)} currency={currency} />
                          </button>
                        );
                      })}
                    </div>
                  )}
                  </>
                )
              )}
              {notePromptLine('style')}
            </div>
          )}
        </div>

        {/* DELIVERY AND RETURNS, UNDER THE CHOICE — not only in the third tab, 1,100px down,
          * where the ad's "free delivery" went unseen (DK Store, 2026-09-17).
          *
          * AFTER the picker, not before it (2026-10-07). The owner: "just to make sure the area
          * of the features is under the options area right? I mean if the product with choices
          * and options they are before the area of features?" — yes, and it was the other way
          * round. The promise is about the ORDER; the colour and the size are about the thing
          * being ordered, and a shopper reading top to bottom settles what they are buying
          * before they care how it arrives. It also keeps the picker directly under the price
          * it changes, which is the reason the picker sits where it does. Built from the
          * shop's own settings; a shop that has set none of the three shows no row. */}
        <DeliveryPromiseLine
          salonSlug={salonSlug}
          /* An ask product has no shipping quote to read (the server refuses it, ORDER_BY_ASKING): nothing is asked. */
          variant={asks ? null : selectedVariant}
          returns={shownReturns}
          freeDeliveryThreshold={freeShippingThreshold}
          currency={currency}
          t={t}
        />

        {/* dk-store's automatic-coupon card, only while a campaign discount
          * really applies to the chosen variant. */}
        <ProductSaleCard pricing={campaignPricing} currency={currency} soldOut={isOutOfStock} />
        {/* A once-per-order amount is not in the unit price above; it is named here (UX-FINAL-11). */}
        <OrderOfferNote
          product={product}
          variant={matchedVariant ?? selectedVariant}
          campaigns={salon?.campaigns}
          currency={currency}
          className={styles.variantTitleInline}
          testId="product-detail-order-offer"
        />

        {/* A STEPPER IS A SPINBUTTON, AND THIS ONE WAS A `<span>`.
          *
          * The two buttons carried names; the number between them carried
          * nothing — no role, no bounds, no keyboard. A shopper on a screen
          * reader met a bare digit and could only change it by finding the
          * discs beside it, which said "increase quantity" without ever
          * saying quantity OF WHAT was at one. So the value takes the role it
          * has always had, is bounded by the stock this shop is actually
          * holding, and answers the up and down keys the role promises.
          *
          * `aria-labelledby` points at the VISIBLE label rather than
          * repeating the word in an `aria-label`: one string, one place, and
          * what is heard is what is on the screen. */}
        {/* Sold out: nothing to count, so no stepper (UX-SHOP-21). The wishlist
          * CTA below is the one thing this product still offers. */}
        {!isOutOfStock && !asks && (
        <div className={styles.qtyBlock} data-testid="product-detail-qty-block">
          <span className={styles.qtyLabel} id="product-detail-qty-label">
            {t('productDetail.quantity', { defaultValue: 'Piece' })}
          </span>
          <div className={styles.qtyControl}>
            <button
              type="button"
              className={`${styles.qtyBtn} ${qtyBump === 'dec' ? styles.qtyBtnBump : ''}`}
              onClick={() => bumpQty('dec')}
              disabled={qty <= 1}
              data-move="qty_dec"
              aria-label={t('productDetail.decrementQty', { defaultValue: 'Decrease quantity' })}
            >
              {/* ONE ICON, DIMMED — NOT A SECOND, NEARLY-WHITE ONE.
                * This swapped to `minus-gray.svg` at qty 1, whose stroke is
                * `#e5e5e8`: 1.17:1 on the page. Paired with a `+` that this
                * product also disables (one left in stock), the whole control
                * read as an empty box, and was reported as "why there's no +
                * and - in the amount". The `−` is drawn, and was drawn; it
                * simply could not be seen. Same glyph as the `+` now, dimmed
                * by the same rule, so "disabled" looks like one state. */}
              <img src={minusIcon} className={`${styles.qtyIcon} ${qty <= 1 ? styles.qtyIconDim : ''}`} alt="" aria-hidden />
            </button>
            <span
              className={`${styles.qtyValue} ${qtyBump ? styles.qtyValueBump : ''}`}
              data-testid="product-detail-qty"
              role="spinbutton"
              tabIndex={0}
              aria-labelledby="product-detail-qty-label"
              aria-valuenow={qty}
              aria-valuemin={1}
              /* Only when the shop counts: a ceiling asserted over an
               * uncounted shelf is a number nobody can honour. */
              {...(tracks && stock > 0 ? { 'aria-valuemax': stock } : {})}
              aria-valuetext={String(qty)}
              /* UP AND DOWN ONLY, DELIBERATELY. A spinbutton's axis is
               * vertical in every reading direction; binding the horizontal
               * arrows here would mean deciding whether "right" is more or
               * less on a Hebrew page, and there is no answer to that which
               * is right in both alphabets. */
              onKeyDown={(e) => {
                if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  if (!(isOutOfStock || isUnavailableCombo || (tracks && qty >= stock))) bumpQty('inc');
                } else if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  if (qty > 1) bumpQty('dec');
                }
              }}
            >
              {qty}
            </span>
            {(() => {
              const plusDisabled = isOutOfStock || isUnavailableCombo || (tracks && qty >= stock);
              return (
                <button
                  type="button"
                  className={`${styles.qtyBtn} ${qtyBump === 'inc' ? styles.qtyBtnBump : ''}`}
                  onClick={() => bumpQty('inc')}
                  data-move="qty_inc"
                  disabled={plusDisabled}
                  aria-label={t('productDetail.incrementQty', { defaultValue: 'Increase quantity' })}
                >
                  <img src={plusIcon} className={`${styles.qtyIcon} ${plusDisabled ? styles.qtyIconDim : ''}`} alt="" aria-hidden />
                </button>
              );
            })()}
          </div>
        </div>
        )}

        {/* Announced, because the button's own words change under the press. */}
        <p className={styles.srOnly} role="status" aria-live="polite" data-testid={capNoteText ? 'product-detail-cap-note' : undefined}>
          {capNoteText || ''}
        </p>

        {/* Desktop-only inline CTA (mobile uses the sticky bottom bar). Out of
         * stock → the wishlist CTA takes this slot instead of a dead button. */}
        {/* The row carries the test id the movement tracker reads, not the buttons inside it: on a
          * phone those buttons are `display: none` (the sticky bar has them) and a hidden
          * element's rect is ALL ZEROS, which the tracker reads as "top of the page". See the
          * note on SECTIONS in utils/shopMovement.js. */}
        <div
          className={styles.inlineCta}
          data-testid="product-detail-buy-row"
        >
          {asks ? (
            /* Sold by asking: the ask IS the buy row. On a phone the docked bar carries it, so this copy is the desktop's. */
            <div className={styles.askInline}>{renderAskOrNoChannel('product-detail-ask', 'ask')}</div>
          ) : isOutOfStock ? (
            renderWishlistCta(styles.inlineAddBtn, 'product-detail-wishlist-inline')
          ) : (
            <>
              <button
                type="button"
                className={`${styles.addToCartBtn} ${styles.inlineAddBtn} ${justAdded ? styles.addToCartBtnAdded : ''} ${waitingForChoice ? styles.addToCartBtnWaiting : ''}`}
                onClick={() => handleAddToCart('add')}
                data-testid="product-detail-add-to-cart-inline"
              >
                {justAdded ? <FiCheck /> : <FiShoppingCart />}
                {/* The desktop's add: its own word only while a choice waits — quick buy beside it names the choice. */}
                {addWord}
              </button>
              {/* Quick buy, the 10-08 button, in the page on every width: it names a missing choice, and while it is on
                  the screen the docked add does not (`quickBuyOnScreen`). */}
              <button
                ref={setQuickBuyEl}
                type="button"
                className={styles.buyNowBtn}
                onClick={() => handleAddToCart('buy')}
                data-testid="product-detail-buy-now-inline"
              >
                {/* IT NAMES THE MISSING CHOICE, LIKE THE DOCKED ONE DID (2026-10-08). This used to be the
                    desktop-only copy and said "רכישה מהירה" whatever the shopper had or had not chosen; the
                    docked Buy now, which a phone actually used, named the pending axis instead. Quick buy is
                    the page's button on every width now, so it inherits that: of 360 people who reached DK's
                    luggage from the ads in the week to 21 Sept 2026, 26 ever touched a colour, and a button
                    that looks ready when it is not is how they found that out. And it is the ONE that names it
                    (run 4, round B: S5): the add buttons say their own word with the choice as a hint line. */}
                {pendingAxisName && !isOutOfStock && !justAdded && !capNoteText
                  ? addToCartLabel
                  : t('productDetail.buyNow', { defaultValue: 'Buy now' })}
              </button>
            </>
          )}
          {!asks && renderContactOrder('product-detail-contact-order')}
        </div>

        {/* SAVE AND SHARE, WHERE THE REFERENCE PUTS THEM.
          *
          * These two were icon discs standing in the headline's line, and they
          * were costing it 92px of a 452px column — which is why the name
          * wrapped to two lines against the reference's one. Measured on
          * `shop.app`: it has nothing beside its name and carries Save and
          * Share as two 228 x 44 pills under "Buy now", filling the column
          * exactly (228 + 8 + 228 = 464). Ours take the same place and the
          * same shape.
          *
          * The label is now VISIBLE, not only announced. Two unlabelled
          * glyphs under a buy button is a guess a shopper has to make, and the
          * heart in particular is read as "favourite", "wishlist" and "like"
          * by three different shops. The `aria-label` stays on the button and
          * still carries the fuller sentence — it wins over the visible text
          * for a screen reader, which is deliberate: "Add to favorites" says
          * what pressing it DOES, where "Save" only names it. */}
        {!actionsOnHero && renderTitleActions('info', ['like', 'share'])}

        {/* THE TABS THE MERCHANT HAS.
          *
          * The owner, of his own shop: "see other tabs in the product page in
          * their website — they have more details about the products. Let's
          * have it also." Theirs carries four — תיאור, משלוחים והחזרות,
          * אחריות and לקוחות מספרים. Ours carried two, and the returns promise
          * was filed under "Description" while the specification sheet that
          * decides a ₪999 watch had no screen at all.
          *
          * So: description, specification, and shipping-and-returns together
          * the way he groups them. Reviews stay their own section below, which
          * is where this page already put them. The specification tab is
          * present only when the shop holds something to put in it.
          *
          * They live inside the info column so on desktop they flow next to
          * the sticky gallery image instead of jumping to a full-width block
          * below. The brand card + related carousel below stay full-width. */}
        <div className={styles.heroDetails}>
          <div ref={setReadingHeadEl} className={styles.readingHead} aria-hidden data-testid="product-detail-reading-head" />
          {infoTabs.length > 0 && (
          <div
            className={styles.infoTabs}
            role="tablist"
            aria-label={t('productDetail.tabsLabel', { defaultValue: 'Product information' })}
            data-testid="product-detail-tabs"
          >
            {infoTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                role="tab"
                id={`product-tab-${tab.key}`}
                aria-selected={activeTab === tab.key}
                aria-controls={`product-panel-${tab.key}`}
                // Roving tabindex: the tab row is ONE tab stop, and the arrow
                // keys move inside it. Three separate stops is how a keyboard
                // user ends up tabbing through a row that looks like one control.
                tabIndex={activeTab === tab.key ? 0 : -1}
                ref={(el) => { tabRefs.current[tab.key] = el; }}
                className={`${styles.infoTabBtn} ${activeTab === tab.key ? styles.infoTabBtnActive : ''}`}
                onClick={() => setActiveTab(tab.key)}
                onKeyDown={onTabKeyDown}
                data-testid={`product-detail-tab-${tab.key}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          )}

          {hasDescription && (
            <div
              className={styles.tabPanel}
              role="tabpanel"
              id="product-panel-description"
              aria-labelledby="product-tab-description"
              tabIndex={0}
              hidden={activeTab !== 'description'}
            >
              <RichDescription
                html={product.descriptionHtml}
                text={product.description}
                t={t}
                isRtl={isRtl}
                id="product-detail-description-copy"
              />
            </div>
          )}

          {specRows.length > 0 && (
            <div
              className={styles.tabPanel}
              role="tabpanel"
              id="product-panel-specs"
              aria-labelledby="product-tab-specs"
              tabIndex={0}
              hidden={activeTab !== 'specs'}
            >
              <SpecificationsSection rows={specRows} isRtl={isRtl} />
            </div>
          )}

          {hasDelivery && (
          <div
            className={styles.tabPanel}
            role="tabpanel"
            id="product-panel-delivery"
            aria-labelledby="product-tab-delivery"
            tabIndex={0}
            hidden={activeTab !== 'delivery'}
          >
            {/* AN ASK PRODUCT IS DELIVERED BY ARRANGEMENT (panel S6): the shipping quote refuses it, and the tab used to read
              * «التوصيل إلى مدينتك — غير متاح» about a giveaway the shop hands over as agreed. No quote is asked for. */}
            {asks ? (
              <div className={styles.askDelivery} data-testid="product-detail-ask-delivery">
                <FiTruck className={styles.askDeliveryIcon} aria-hidden />
                <span className={styles.askDeliveryTitle}>{t('shipping.deliveryAndPickup', { defaultValue: 'Delivery and pickup' })}</span>
                <span className={styles.askDeliveryValue}>{t('shipping.byArrangement', { defaultValue: 'By arrangement with the shop' })}</span>
              </div>
            ) : (
              <ShippingInfoSection
                salonSlug={salonSlug}
                variant={selectedVariant}
                cartTotal={cartTotal}
                t={t}
              />
            )}
            {/* Returns belong beside shipping, which is where the merchant
              * himself puts them — his tab is called "משלוחים והחזרות". They
              * used to sit under the description, below the spec sheet. */}
            {hasReturns && (
              <ReturnPolicySection
                policy={shownReturns}
                entry={rowReturnPolicy ? null : product?.returnPolicyLibrary}
                t={t}
                isRtl={isRtl}
                ForwardChevron={ForwardChevron}
              />
            )}
          </div>
          )}

          {/* His fourth tab. Present only when a warranty resolves — from the
            * row, the product, the shelf or the shop — and absent, panel and
            * all, when none does. */}
          {shownWarranty && (
            <div
              className={styles.tabPanel}
              role="tabpanel"
              id="product-panel-warranty"
              aria-labelledby="product-tab-warranty"
              tabIndex={0}
              hidden={activeTab !== 'warranty'}
            >
              <WarrantySection
                warranty={shownWarranty}
                /* The saved entry that answered — it is what carries the
                 * FILE. Absent when a row typed its own words, and absent on
                 * the variant link, because only a level that can point at
                 * the library can have one. */
                entry={rowWarranty ? null : product?.warrantyLibrary}
                t={t}
                isRtl={isRtl}
              />
            </div>
          )}

          {/* THE BRAND, WHERE THE BRAND BELONGS — and the business's mark
            * demoted to the business's own row underneath it.
            *
            * The owner: "why is our logo here, in the side? (I mean the logo
            * of the biz)". The card below was the only brand-shaped thing on
            * this page and it was rendering `salonLogoUrl`, on products whose
            * payload has carried the whole `brand` row all along. Both stay,
            * because they are two different destinations — this brand's page,
            * and the whole catalogue. ("Back" used to be a bare
            * `navigate(-1)`, which for a shared link went nowhere — from an ad
            * it walked the person out to Facebook. It now falls back to this
            * same brand page; see backFromHere.js.) Only the order of loudness
            * changes, and only on a product that HAS a brand. */}
          <ProductBrandRow
            brand={product.brand}
            salonSlug={salonSlug}
            language={i18n.language}
            t={t}
            isRtl={isRtl}
          />

          {/* THE SHOP, AS A PLACE — NOT AS A LINE OF TEXT.
            *
            * The owner, with the reference beside ours: "we had the page item
            * in there different way, with background of the cover pic of the
            * item itself and inside it the logo, see shop.app". Theirs is a
            * card with the shop's cover photograph behind it, the mark on the
            * cover, and the name, rating and the way in sitting on a scrim
            * over the picture. Ours was an avatar, a name and a chevron on the
            * page's own white — correct, and completely unmemorable.
            *
            * ── WHEN THE SHOP HAS NO PICTURE ──────────────────────────────
            *
            * dk-store has no cover AND no logo (both null on the public salon
            * payload), so the picture path is the exception here, not the
            * rule. A cover-shaped hole is worse than no card, so the fallback
            * is composed rather than empty: the page's own accent laid down as
            * a soft wash, and the monogram enlarged onto it. It reads as a
            * designed panel instead of a missing asset — and the moment the
            * shop uploads a cover, the photograph takes the same slot with no
            * other change.
            *
            * The CTA is a `span`, not a button: the whole card is already the
            * button, and a control inside a control is unreachable for half
            * the people using one. */}
          <button
            type="button"
            ref={shopCardRef}
            className={`${styles.shopCard} ${salonCoverUrl ? '' : styles.shopCardNoCover}`}
            dir={isRtl ? 'rtl' : 'ltr'}
            onClick={() => navigate(`${getBasePath(salonSlug)}/shop`)}
            data-testid="product-detail-visit-shop"
          >
            {/* An <img> that loads lazily, not a CSS background: the card sits at the foot of the page, and a
                background is fetched the moment the page paints — 68 KB beside the hero on an ad landing
                (measured 2026-10-01). A lazy picture at the foot of the page waits until the shopper nears it. */}
            <span className={styles.shopCardCover} aria-hidden>
              {salonCoverUrl && shopCardNear ? <img src={salonCoverUrl} alt="" className={styles.shopCardCoverImg} loading="lazy" fetchpriority="low" decoding="async" /> : null}
            </span>
            <span className={styles.shopCardScrim} aria-hidden />
            <span className={styles.shopCardMark} aria-hidden>
              {salonLogoUrl ? (
                <img src={salonLogoUrl} alt="" className={styles.shopCardMarkImg} loading="lazy" fetchpriority="low" decoding="async" />
              ) : (
                <span className={styles.shopCardMonogram}>
                  {(salon?.name || salonSlug || '?').charAt(0).toUpperCase()}
                </span>
              )}
            </span>
            <span className={styles.brandText}>
              <span className={styles.brandName}>
                {salon?.name || salonSlug}
                {/* THE SHOP'S RATING, BESIDE THE SHOP'S NAME — the reference's
                  * `shop-name-title-rating-tag-container`, measured as the
                  * VALUE, one 12px filled star, then the count in brackets,
                  * in the page's own ink.
                  *
                  * It is the BUSINESS's rating (service reviews and product
                  * reviews pooled), not this product's and not Google's. Each
                  * run is its own `<bdi>`: a Latin "4.3" and a bracketed count
                  * inside a Hebrew or Arabic name is the textbook bidi
                  * reordering. */}
                {businessRating.count > 0 && (
                  <span className={styles.shopRating} data-testid="product-detail-shop-rating">
                    <bdi className={styles.shopRatingValue}>{formatRatingValue(businessRating.average)}</bdi>
                    <FreshaStar size={12} color="currentColor" />
                    <bdi className={styles.shopRatingCount}>({businessRating.count})</bdi>
                  </span>
                )}
              </span>
              <span className={styles.brandLink}>
                {t('productDetail.allProducts', { defaultValue: 'All products' })}
                <ForwardChevron aria-hidden />
              </span>
            </span>
            <span className={styles.shopCardCta} aria-hidden>
              {t('productDetail.visitShop', { defaultValue: 'Visit shop' })}
            </span>
          </button>
        </div>
      </div>
      </div>

      <div className={styles.postHero} data-testid="product-detail-post-hero">
        {reviewsPublic && reviewSummary.count > 0 && (
          <ReviewsSection
            ref={reviewsSectionRef}
            productId={product.id}
            summary={reviewSummary}
            onViewAll={() => setReviewsModalOpen(true)}
            t={t}
            shopName={salon?.name}
            shopLogo={salonLogoUrl}
            product={reviewProduct}
            productHref={reviewProductHref}
          />
        )}
        {/* WHAT CUSTOMERS SAY ABOUT THE SHOP — the business's own reviews,
          * transcribed from its Google listing, each labelled with its source.
          * After this product's own reviews when it has any, and in their
          * place when it has none: "show them on the product page somewhere
          * if the review isn't on the product itself". The three it shows
          * arrived inside the salon payload above — no request on first paint. */}
        <ShopReviewsBlock
          shopReviews={salon?.shopReviews}
          salonSlug={salonSlug}
          shopName={salon?.name}
          t={t}
        />
        <LazyRelated
          salonSlug={salonSlug}
          productId={product.id}
          campaigns={salon?.campaigns}
          onAddToCart={(p, variant, shownPrice) => {
            window.dispatchEvent(new CustomEvent('stylon:add-to-cart', {
              detail: {
                salonSlug, productId: p.id, variantId: variant.id, quantity: 1,
                // The price the card showed for that option, for the pixel (Components/RecMiniCard).
                shownPrice,
                snapshot: {
                  name: p.name + (variant.name ? ` — ${variant.name}` : ''),
                  image: variant.images?.[0]?.url ?? null,
                  // Related products are a raw shape — inherit-priced variants
                  // resolve to the main variant's defaultPrice (never snapshot ₪0).
                  price: Number(variant.price ?? variant.mainVariant?.defaultPrice ?? 0),
                  // WHAT THE SHOP SAID IT HAS, carried with the line so the cart can
                  // refuse the 41st click without a round trip. `null` when the shop
                  // states no ceiling — see `utils/stockCap.js`.
                  maxQty: variantPurchaseCap(p, variant),
                  variantAttrs: {
                    color: variant.color ?? null,
                    size:  variant.size  ?? null,
                    style: variant.style ?? null,
                  },
                  options: variantOptionsIn(p, variant, i18n.language, t),
                },
              },
            }));
            // The bottom cart bar steps aside on a phone here, so the drawer is the confirmation.
            window.dispatchEvent(new CustomEvent('stylon:open-cart', { detail: { addedVariantId: variant.id } }));
          }}
        />
        {/* The end of the page's own column — above the room `.page` leaves for the sticky
          * add-to-cart bar on a phone, so the bar never covers it. */}
        <PrivacyNotice t={t} />
      </div>

      <div className={styles.stickyCta} ref={stickyCtaRef} data-testid="product-detail-sticky-cta">
        {/* THE BAG RIDES IN THE BAR, BESIDE THE BUTTON THAT FILLS IT.
          *
          * It used to be a 48px disc fixed to the top corner of a phone screen,
          * and once the page scrolled it sat on the sale box's own words
          * ("15% הנחה אוטומטית על כל האתר", DK Store, 2026-09-16). The bottom
          * cart bar steps aside on this page, so the way back into the cart
          * lives here, where nothing is printed under it. Hidden from 900px
          * with the rest of this bar; the cart bar shows there instead. */}
        <div className={`${styles.stickyCtaInner} ${cartCount > 0 ? styles.stickyCtaWithBag : ''}`}>
          {asks ? (
            renderAskOrNoChannel('product-detail-ask-sticky', 'ask_bar')
          ) : isOutOfStock ? (
            renderWishlistCta('', 'product-detail-wishlist-sticky')
          ) : (
            <>
              {/* QUICK BUY LEFT THIS BAR FOR THE PAGE (2026-10-08). The owner: "I still see quick buy
                  button in the bottom area with the add to cart.. and why the cart is there and it's
                  very big? enough one icon", then, settling it himself: "it will be there the button in
                  the page not in the docked area of actions". Three controls in a 390px bar — buy, add,
                  bag — with the two biggest saying nearly the same thing, is what he was looking at.

                  AND THAT IS WHY THE LEADING BUTTON IS NOT SIMPLY "ADD" (the hole this would otherwise
                  re-open). On 2026-10-07 I refused to HIDE quick buy for exactly this reason and wrote
                  the reason down here: quick buy ENSURES one line, add INCREMENTS, so promoting add makes
                  the easiest tap on the screen cumulative — and that is the 2026-10-02 sequence that asked
                  a shopper for ₪2,460 when they meant to pay ₪492, after a failed payment sent them back
                  to this product. The note also said what the right answer was and that it was not built:
                  "the leading button to become 'go to the basket' rather than 'add'". It is built now.

                    nothing in the basket → ADD leads, and there is nothing to double.
                    something in it     → GO TO THE BASKET leads, in words, and add steps back to the
                                          icon beside it. The easy tap finishes; the cumulative one is
                                          still there for a second product, but it is no longer first.

                  Quick buy itself is now in the page, where he asked for it, and it is still the
                  idempotent one — so the safe button did not disappear, it moved. */}
              {/* ADD LEADS, ALWAYS — second arm (2026-10-12, the owner: "why showing in the bottom area when there's stuff
                  in the cart a button highlighted of go to the cart while having also the icon of the cart! add should be
                  the highlighted button no?"). The bag beside it, with its count, is the way into the basket; the drawer
                  opening on every add is what shows a second add for what it is. The first arm keeps the 2026-10-07
                  bar: with a basket, "go to the basket" leads and add steps back beside it. */}
              {!leanCheckout && cartCount > 0 ? (
                <button
                  type="button"
                  className={`${styles.addToCartBtn} ${styles.buyNowBtn} ${styles.stickyBuy}`}
                  onClick={() => window.dispatchEvent(new CustomEvent('stylon:open-cart'))}
                  data-testid="product-detail-go-to-cart"
                >
                  {/* "מעבר לסל" — the same words the bag beside it already carries as its name, and the
                      shop's own existing phrase rather than a new one to translate three times. */}
                  {t('productDetail.viewCart', { defaultValue: 'View cart' })}
                </button>
              ) : null}
              <button
                type="button"
                /* It leads the bar (10-08), so it keeps the primary look while a choice waits — never the waiting grey
                 * (fix round 1, #3) — and names the choice only while quick buy is off the screen. */
                className={`${styles.addToCartBtn} ${!leanCheckout && cartCount > 0 ? styles.stickyAddSecondary : styles.stickyBuy} ${justAdded ? styles.addToCartBtnAdded : ''}`}
                onClick={() => handleAddToCart('add')}
                data-testid="product-detail-add-to-cart"
                data-asks={dockedAsks ? 'choice' : undefined}
                aria-label={dockedAddName}
                title={dockedAddName}
              >
                {justAdded ? <FiCheck /> : <FiShoppingCart />}
                {/* "לסל" is two words short of a sentence and the owner said so: "what's the meaning
                    of to cart? add to cart? it's not clear". Beside the basket button it has the
                    context; leading the bar it has none, so it says the whole thing. */}
                <span className={styles.stickyAddText}>
                  {dockedAsks ? waitingWordsFor(dockedDoor) : dockedDoor}
                </span>
              </button>
            </>
          )}
          {/* AND A WAY INTO THE BASKET, IN WORDS (2026-10-07). The owner asked for quick buy to go
              once the basket has something in it. It cannot — hiding it promotes ADD, which
              increments, and that is the 2026-10-02 sequence that asked a shopper for ₪2,460
              instead of ₪492. What he actually wants is right: a basket in play should not leave
              the shopper staring at two ways to add and no way to finish. So the bag stops being
              a mute icon and says where it goes, with the count beside it. */}
          {cartCount > 0 && (
            <button
              type="button"
              className={`${styles.barBag} ${cartBump ? styles.barBagBump : ''}`}
              onClick={() => window.dispatchEvent(new CustomEvent('stylon:open-cart'))}
              aria-label={`${t('productDetail.viewCart', { defaultValue: 'View cart' })} (${cartCount})`}
              data-testid="cart-top-floating"
            >
              <span
                className={styles.barBagIcon}
                style={{ maskImage: `url(${basketIcon})`, WebkitMaskImage: `url(${basketIcon})` }}
                aria-hidden
              />
              <span className={styles.barBagBadge} data-testid="cart-top-floating-count" aria-hidden>{cartCount}</span>
            </button>
          )}
        </div>
      </div>

      {/* WHATSAPP FLOATS, IT NO LONGER TAKES A ROW (2026-10-07, the owner: "instead maybe of the
          area of order with whatsapp, just show the floating whatsapp circle in the page (smaller)
          but there"). It sits outside the bar so the bar belongs entirely to the two buttons that
          sell, and because it costs no space it is shown whether or not there is a basket — the
          earlier "hide it once there is a basket" was about crowding, and the crowding is gone.
          The draft it opens is unchanged: productOrderMessage already writes the product's name,
          the chosen options, the quantity, the price and the product's own link into the text. */}
      {contactOrder.any && !asks && !inlineAskOnScreen && (
        /* Stepping aside for the reading FADES (fix round 1, DESIGN_SYSTEM.md §6): kept mounted, faded and untappable. */
        <div
          className={styles.floatingContact}
          data-testid="product-detail-floating-contact"
          data-aside={floatersAside ? '' : undefined}
          aria-hidden={floatersAside || undefined}
        >
          {waBubble && (
            <button
              type="button"
              className={styles.floatingContactBubble}
              onClick={() => setWaBubble(false)}
              data-testid="product-detail-contact-order-bubble"
            >
              {t('contactOrder.bubble', { defaultValue: 'You can order right here on WhatsApp' })}
            </button>
          )}
          {renderContactOrder('product-detail-contact-order-sticky', 'floating')}
        </div>
      )}

      <ReviewsModal
        open={reviewsModalOpen && reviewsPublic}
        productId={product.id}
        product={reviewProduct}
        productHref={reviewProductHref}
        onClose={() => setReviewsModalOpen(false)}
        t={t}
        shopName={salon?.name}
        shopLogo={salonLogoUrl}
      />

      {/* The sheet starts from what the shopper CHOSE (a tap here, or the ad link they came by) — never from the
        * variant the page shows for want of a choice (panel S2): the cookies' sheet opened «دبي» selected and added it. */}
      <ColorPickerModal
        open={colorModalOpen}
        product={product}
          pickedColor={shopperChose('color') ? pickedColor : null}
          setPickedColor={sheetPicks.color}
          pickedSize={shopperChose('size') ? pickedSize : null}
          setPickedSize={sheetPicks.size}
          pickedStyle={shopperChose('style') ? pickedStyle : null}
          setPickedStyle={sheetPicks.style}
          onClose={() => setColorModalOpen(false)}
          onAddToCart={() => handleAddToCart(choiceMode, { chosen: true })}
          cta={choiceMode}
          t={t}
        />

      <InAppBrowserPrompt open={promptOpen} os={phoneOsHere} onOpen={promptTap} onStay={promptStay} swallowed={promptSwallowed} />

      <ProductMediaViewer
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        images={images}
        alt={product.name}
        index={galleryIndex}
        onIndexChange={(i) => { if (viewerOpen && i !== galleryIndex) galleryStep('viewer', 'viewer_move', i); setGalleryIndex(i); }}
        optionLabel={viewerPicker.optionLabel}
        selectedLabel={viewerPicker.selectedLabel}
        options={viewerPicker.options}
        onPrimary={viewerOnPrimary}
        primaryLabel={viewerPrimaryLabel}
        primaryIcon={viewerPrimaryIcon}
      />

      {flyClone && <FlyToCartClone clone={flyClone} />}
    </div>
  );
};

/** How long the picture takes to land on the bag; the drawer opens after it. */
const FLY_TO_CART_MS = 700;

function FlyToCartClone({ clone }) {
  const [arrived, setArrived] = useState(false);
  useEffect(() => {
    // Double rAF to ensure the start transform is committed before the
    // transition flips to the end transform.
    let id1, id2;
    id1 = requestAnimationFrame(() => {
      id2 = requestAnimationFrame(() => setArrived(true));
    });
    return () => {
      cancelAnimationFrame(id1);
      if (id2) cancelAnimationFrame(id2);
    };
  }, []);
  if (typeof document === 'undefined') return null;
  const { src, size, fromX, fromY, toX, toY } = clone;
  const half = size / 2;
  const startTransform = `translate3d(${fromX - half}px, ${fromY - half}px, 0) scale(1) rotate(0deg)`;
  const endTransform = `translate3d(${toX - half}px, ${toY - half}px, 0) scale(0.18) rotate(35deg)`;
  return createPortal(
    <div
      aria-hidden
      data-testid="product-fly-clone"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: size,
        height: size,
        borderRadius: '20%',
        overflow: 'hidden',
        boxShadow: '0 18px 40px rgba(11, 15, 52, 0.35), 0 4px 12px rgba(11, 15, 52, 0.18)',
        zIndex: 9999,
        pointerEvents: 'none',
        background: '#fff',
        transform: arrived ? endTransform : startTransform,
        opacity: arrived ? 0 : 1,
        transition: 'transform 0.72s cubic-bezier(0.32, 0.05, 0.06, 1), opacity 0.72s cubic-bezier(0.4, 0, 0.6, 1)',
        willChange: 'transform, opacity',
      }}
    >
      <img
        src={src}
        alt=""
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    </div>,
    document.body,
  );
}

/**
 * The returns promise, already resolved. The chain was walked upstream —
 * variant here on the page, then product → category → business on the server —
 * so this states ONE policy and never falls back to a second one behind it.
 */
function ReturnPolicySection({ policy, t, isRtl, ForwardChevron, entry = null }) {
  // Our own default opens by itself: the shop published nothing, so the one
  // paragraph there is IS the policy, and hiding it behind a chevron would
  // make the section look like it was holding something back.
  const [open, setOpen] = React.useState(!!policy && policy.isDefault);
  if (!policy) return null;
  const allowed = policy.allowed;
  const blurb = hasProse(policy.blurb) ? policy.blurb : null;
  const days = policy.windowDays;
  /**
   * `allowed === null` is the platform default, and it prints NO summary at
   * all. "Returns accepted" would be us promising on a merchant's behalf and
   * "Final sale" would be us refusing on theirs; the paragraph below states
   * the statutory position and claims nothing either way.
   */
  const summaryLine = returnsSummaryLine({ allowed, windowDays: days }, t);
  return (
    <div className={styles.policySection} data-testid="product-detail-return-policy">
      <button
        type="button"
        className={styles.policyHeader}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid="product-detail-return-policy-toggle"
      >
        <span className={styles.policyTitle}>
          {t('productDetail.returnPolicy.title', { defaultValue: 'Returns & exchanges' })}
        </span>
        <ForwardChevron className={`${styles.policyChevron} ${open ? styles.policyChevronOpen : ''}`} aria-hidden />
      </button>
      {summaryLine && <div className={styles.policySummary}>{summaryLine}</div>}
      {open && blurb && (
        <PolicyProse
          blurb={blurb}
          isRtl={isRtl}
          className={styles.policyBlurb}
          testId="product-detail-return-policy-text"
        />
      )}
      {open && (
        <PolicyDocumentLink entry={entry} t={t} testId="product-detail-return-policy-document" />
      )}
    </div>
  );
}

/**
 * A CHOICE'S OWN PRICE, UNDER ITS NAME (D1-24) — only where its slot's values cost differently. One left-to-right run,
 * as `PriceTag`'s, so «70 ₪» never turns inside a right-to-left tile.
 */
function ChoicePrice({ id, amount, currency }) {
  if (amount == null) return null;
  // Drawn for the eye; read out as the button's description (`aria-describedby`), so the button is still named «S».
  return (
    <span id={id} className={styles.choicePrice} data-testid="product-detail-choice-price" dir="ltr" aria-hidden="true">
      {groupedShopAmount(amount, currency)}
      {' '}
      {currencySymbol(currency)}
    </span>
  );
}

/**
 * A CHOICE TILE'S NAME, WHOLE (Swe Sosetta run 6, round D: S1). At most two lines of whole words, never «…»: the one
 * break is `utils/tileLabel`'s, each line is an unbroken box, and the tile is as wide as its longer line. The
 * `data-reserve` copy is drawn hidden and semibold in the same cell, so the tile keeps one size chosen or not.
 */
function TileName({ label }) {
  const lines = tileLabelLines(label);
  return (
    <span className={styles.colorThumbName} data-reserve={tileLabelReserve(lines)}>
      <bdi>
        {lines.map((line, i) => (
          <React.Fragment key={`${i}:${line}`}>
            {i > 0 ? ' ' : null}
            <span className={styles.colorThumbLine}>{line}</span>
          </React.Fragment>
        ))}
      </bdi>
    </span>
  );
}

/**
 * Shopier-style price tag: big bold integer, small currency suffix, and an
 * optional fractional sub-span. Splits the number so each part can be sized
 * independently in CSS.
 */
function PriceTag({ amount, currency, className, testId }) {
  /* ONE NUMBER, IN ONE DIRECTION (UX-SHOP-7).
   *
   * The whole part and the cents are sized apart, so they are two spans — and
   * in a right-to-left line two spans of digits are two runs the bidi
   * algorithm is free to reorder: "162.50 ₪" came out as "162 50. ₪". The tag
   * is an isolated LTR run (`dir="ltr"`), and the two parts sit in ONE box so
   * the gap belongs between the number and its symbol, not inside the number.
   * The digits are `groupedShopAmount`'s — whole when whole, the currency's
   * own decimals when not (a JOD price keeps three), never rounded away. */
  const [whole, fraction] = groupedShopAmount(amount, currency).split('.');
  return (
    <span className={`${styles.priceTag} ${className || ''}`} data-testid={testId} dir="ltr">
      <span className={styles.priceNumber}>
        <span className={styles.priceValue}>{whole}</span>
        {fraction && <span className={styles.priceCent}>.{fraction}</span>}
      </span>
      {' '}
      <span className={styles.priceCurrency}>{currencySymbol(currency)}</span>
    </span>
  );
}

/** A copy of the chosen-axes set without one axis (state is never mutated). */
function withoutAxis(set, axis) {
  const next = new Set(set);
  next.delete(axis);
  return next;
}

function uniqOf(variants, key) {
  if (!variants?.length) return [];
  const set = new Set();
  for (const v of variants) {
    const val = v[key];
    if (val) set.add(val);
  }
  return Array.from(set);
}

// A swatch's colour is utils/colourSwatch's (the value's hex, else its own presets and CSS names, else a ring) —
// fix round 2, #2; this file kept a copy of the presets.

export default ProductDetail;
